import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import Layout from '../components/layout/Layout';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
// import { Table } from '../components/common';
import ResponsiveTable from '../components/common/ResponsiveTable';
import MobileCardList, { TableColumn } from '../components/common/MobileCardList';
import { warehouseService } from '../services/warehouseService';
import { toast } from '../contexts/ToastContext';
import { FIELD_VALIDATORS, INPUT_SANITIZERS, VALIDATION_PATTERNS, validateField } from '../utils/validationHelpers';

type TabKey = 'zones' | 'racks' | 'manage';

interface Warehouse {
  id: number;
  name: string;
  code: string | null;
  address: string;
  totalSpaceSqft: number;
  status: string;
  companyName?: string | null;
  companyGstin?: string | null;
  companyPan?: string | null;
  bankName?: string | null;
  bankAccountNo?: string | null;
  bankIfsc?: string | null;
  bankBranch?: string | null;
  companyTagline?: string | null;
  companyEmail?: string | null;
  companyJurisdiction?: string | null;
}

interface Zone {
  id: number;
  code: string;
  name?: string | null;
  zoneType: string;
  status: string;
  areaSqft?: number | null;
  locationSizeSqft?: number | null;
}

const SQFT_PER_PALLET = 25;

// Hard cap on pallet slots for a single zone or rack — applies to both the
// zone "Area Allocation" field (sqft ÷ pallet size) and the rack "Rack
// Count" field (a direct pallet count).
const MAX_ZONE_PALLETS = 500;
const MAX_RACK_PALLETS = 500;

// Minimum Pallet Size for both zones and racks — supersedes the old
// LBD-only "at least 12 sq ft" floor, which is now unreachable (12 < 25).
const MIN_PALLET_SIZE_SQFT = 25;

// Digits + at most one decimal point, for sqft-style fields (pallet size,
// area allocation) — strips letters/signs a native type="number" input
// would otherwise let through.
const sanitizeDecimalQty = (value: string): string => {
  const cleaned = value.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
};

interface Rack {
  id: number;
  code: string;
  name?: string | null;
  status: string;
  locationSizeSqft?: number | null;
  rackCount?: number | null;
  areaSqft?: number | null;
}

interface PalletRecord {
  id: number;
  inventoryId: number;
  clientId: number;
  clientName: string;
  productDescription: string;
  batchNo: string;
  storageType: 'ground' | 'rack';
  zoneId: number | null;
  zoneCode: string | null;
  zoneLocationId: number | null;
  zoneLocationCode: string | null;
  rackId: number | null;
  rackCode: string | null;
  locationId: number | null;
  locationCode: string | null;
  palletCount: number;
  palletSizeSqft: number | null;
  createdAt: string;
  goodQty: number;
}

export default function WarehouseManagement() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('zones');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [selectedRackId, setSelectedRackId] = useState<number | null>(null);
  // Location Management section — scrolled into view when a rack's "Locations" button is clicked
  const locationSectionRef = useRef<HTMLDivElement>(null);
  const [rackLocationSearch, setRackLocationSearch] = useState('');
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  // Zone Location Management section — scrolled into view when a zone's "Locations" button is clicked
  const zoneLocationSectionRef = useRef<HTMLDivElement>(null);
  const [zoneLocationSearch, setZoneLocationSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [warehouseForm, setWarehouseForm] = useState({
    name: '',
    code: '',
    address: '',
    totalSpaceSqft: '',
    companyName: '',
    companyGstin: '',
    companyPan: '',
    bankName: '',
    bankAccountNo: '',
    bankIfsc: '',
    bankBranch: '',
    companyTagline: '',
    companyEmail: '',
    companyJurisdiction: '',
  });
  const [warehouseBillingErrors, setWarehouseBillingErrors] = useState<Record<string, string>>({});

  const [isEditingCode, setIsEditingCode] = useState(false);
  const [editingCodeValue, setEditingCodeValue] = useState('');

  const [isEditingBillingDetails, setIsEditingBillingDetails] = useState(false);
  const [billingDetailsForm, setBillingDetailsForm] = useState({
    companyName: '',
    companyGstin: '',
    companyPan: '',
    bankName: '',
    bankAccountNo: '',
    bankIfsc: '',
    bankBranch: '',
    companyTagline: '',
    companyEmail: '',
    companyJurisdiction: '',
  });
  const [billingDetailsErrors, setBillingDetailsErrors] = useState<Record<string, string>>({});

  const [zoneForm, setZoneForm] = useState({
    code: '',
    name: '',
    zoneType: 'regular',
    status: 'active',
    areaSqft: '',
    locationSizeSqft: '',
  });
  const [zoneAreaUnit, setZoneAreaUnit] = useState<'sqft' | 'pallets'>('sqft');

  const [rackForm, setRackForm] = useState({
    code: '',
    name: '',
    rackType: 'regular',
    status: 'active',
    locationSizeSqft: '',
    rackCount: '',
  });

  const [locationsForm, setLocationsForm] = useState({
    count: '',
  });

  const getErrorMessage = (error: any, fallback: string) => {
    // Handle validation errors with errors array
    if (error?.errors && Array.isArray(error.errors) && error.errors.length > 0) {
      return error.errors.join(', ');
    }
    // Handle regular error messages
    return error?.message || fallback;
  };

  const {
    data: warehousesResponse,
  } = useQuery({
    queryKey: ['warehouses', 'management'],
    queryFn: () => warehouseService.getWarehouses({ limit: 100 }),
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });

  const warehouses: Warehouse[] = useMemo(() => {
    // Backend returns: { message: string, data: { items: Warehouse[], total: number } }
    const items = warehousesResponse?.data?.items || warehousesResponse?.items || [];
    return items;
  }, [warehousesResponse]);

  useEffect(() => {
    if (!selectedWarehouseId && warehouses.length > 0) {
      setSelectedWarehouseId(warehouses[0].id);
      setSelectedWarehouse(warehouses[0]);
    }
  }, [selectedWarehouseId, warehouses]);

  useEffect(() => {
    if (!selectedWarehouseId) {
      setSelectedWarehouse(null);
      return;
    }
    const found = warehouses.find((warehouse) => warehouse.id === selectedWarehouseId) || null;
    setSelectedWarehouse(found);
  }, [selectedWarehouseId, warehouses]);

  // Infinite query for zones with pagination
  const {
    data: zonesData,
    isError: zonesError,
    error: zonesErrorData,
    fetchNextPage: fetchNextZonesPage,
    hasNextPage: hasNextZonesPage,
    isFetchingNextPage: isFetchingNextZonesPage,
    // isLoading: isLoadingZones,
  } = useInfiniteQuery({
    queryKey: ['zones', 'management', selectedWarehouseId],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await warehouseService.getZones(selectedWarehouseId!, { 
        page: pageParam, 
        limit: 25 
      });
      const items = response?.data?.items || response?.items || [];
      const total = response?.data?.meta?.total ?? response?.meta?.total ?? items.length;
      return { items, total, page: pageParam };
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return totalFetched < lastPage.total ? allPages.length + 1 : undefined;
    },
    enabled: !!selectedWarehouseId,
    initialPageParam: 1,
    staleTime: 30000,
    gcTime: 60000,
  });

  // Warehouse space utilization — total capacity vs. area assigned to zones
  const { data: utilizationResponse } = useQuery({
    queryKey: ['warehouse-utilization', selectedWarehouseId],
    queryFn: () => warehouseService.getWarehouseUtilization(selectedWarehouseId!),
    enabled: !!selectedWarehouseId,
    staleTime: 0,
  });
  const utilization: {
    totalSpaceSqft: number;
    assignedSqft: number;
    zoneSqft: number;
    rackSqft: number;
    availableSqft: number;
    utilizationPercent: number;
    zoneCount: number;
    rackCount: number;
  } | null = utilizationResponse?.data ?? utilizationResponse ?? null;

  // Infinite query for racks with pagination
  const {
    data: racksData,
    isError: racksError,
    error: racksErrorData,
    fetchNextPage: fetchNextRacksPage,
    hasNextPage: hasNextRacksPage,
    isFetchingNextPage: isFetchingNextRacksPage,
    // isLoading: isLoadingRacks,
  } = useInfiniteQuery({
    queryKey: ['racks', 'management', selectedWarehouseId],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await warehouseService.getRacks(selectedWarehouseId!, { 
        page: pageParam, 
        limit: 25 
      });
      const items = response?.data?.items || response?.items || [];
      const total = response?.data?.meta?.total ?? response?.meta?.total ?? items.length;
      return { items, total, page: pageParam };
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return totalFetched < lastPage.total ? allPages.length + 1 : undefined;
    },
    enabled: !!selectedWarehouseId,
    initialPageParam: 1,
    staleTime: 30000,
    gcTime: 60000,
  });

  const zones: Zone[] = useMemo(() => {
    if (!zonesData) return [];
    return zonesData.pages.flatMap(page => page.items || []);
  }, [zonesData]);
  
  const racks: Rack[] = useMemo(() => {
    if (!racksData) return [];
    return racksData.pages.flatMap(page => page.items || []);
  }, [racksData]);

  useEffect(() => {
    if (!selectedRackId && racks.length > 0) {
      setSelectedRackId(racks[0].id);
    }
  }, [selectedRackId, racks]);

  useEffect(() => {
    if (!selectedZoneId && zones.length > 0) {
      setSelectedZoneId(zones[0].id);
    }
  }, [selectedZoneId, zones]);

  // Infinite query for zone locations with pagination
  const {
    data: zoneLocationsData,
  } = useInfiniteQuery({
    queryKey: ['zone-locations', 'management', selectedZoneId],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await warehouseService.getZoneLocations(selectedZoneId!, {
        page: pageParam,
        limit: 25,
      });
      const items = response?.data?.items || response?.items || [];
      const total = response?.data?.meta?.total ?? response?.meta?.total ?? items.length;
      return { items, total, page: pageParam };
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return totalFetched < lastPage.total ? allPages.length + 1 : undefined;
    },
    enabled: !!selectedZoneId,
    initialPageParam: 1,
    staleTime: 30000,
    gcTime: 60000,
  });

  const totalZoneLocationsCount = useMemo(() => {
    if (!zoneLocationsData || !zoneLocationsData.pages || zoneLocationsData.pages.length === 0) return 0;
    return zoneLocationsData.pages[0].total || 0;
  }, [zoneLocationsData]);

  // Zone locations with pallet occupancy (Zones tab → Locations button)
  const {
    data: zoneOccupancyResponse,
    isLoading: zoneOccupancyLoading,
    isError: zoneOccupancyError,
    error: zoneOccupancyErrorData,
  } = useQuery({
    queryKey: ['zone-locations-occupancy', selectedZoneId],
    queryFn: () => warehouseService.getZoneLocationsWithOccupancy(selectedZoneId!),
    enabled: !!selectedZoneId && activeTab === 'zones',
    staleTime: 30000,
  });

  const zoneOccupancyLocations: Array<{
    id: number;
    locationCode: string;
    status: 'occupied' | 'available';
    occupiedPercentage: number;
    availableSqFt: number | null;
    locationSizeSqFt: number | null;
    clientName: string | null;
    productDescription: string | null;
    batchNo: string | null;
  }> = useMemo(() => {
    const raw = zoneOccupancyResponse?.locations ?? zoneOccupancyResponse?.data?.locations ?? [];
    const zoneSizeSqFt: number | null =
      zoneOccupancyResponse?.locationSizeSqft ?? zoneOccupancyResponse?.data?.locationSizeSqft ?? null;
    return raw.map((l: any) => ({
      id: l.locationId,
      locationCode: l.locationCode,
      status: l.status,
      occupiedPercentage: l.occupiedPercentage ?? (l.status === 'occupied' ? 100 : 0),
      availableSqFt: l.availableSqFt ?? null,
      locationSizeSqFt: l.locationSizeSqFt ?? zoneSizeSqFt,
      clientName: l.occupiedBy?.clientName ?? null,
      productDescription: l.occupiedBy?.productQuantities ?? l.occupiedBy?.productDescription ?? null,
      batchNo: l.occupiedBy?.batchNo ?? null,
    }));
  }, [zoneOccupancyResponse]);

  // Clear the search whenever a different zone is selected — a leftover
  // term would otherwise silently hide every row of the newly-picked zone.
  useEffect(() => {
    setZoneLocationSearch('');
  }, [selectedZoneId]);

  const filteredZoneOccupancyLocations = useMemo(() => {
    const term = zoneLocationSearch.trim().toLowerCase();
    if (!term) return zoneOccupancyLocations;
    return zoneOccupancyLocations.filter((l) =>
      [l.locationCode, l.status === 'occupied' ? 'occupied' : 'available', l.clientName, l.productDescription, l.batchNo]
        .some((field) => field?.toLowerCase().includes(term)),
    );
  }, [zoneOccupancyLocations, zoneLocationSearch]);

  // Infinite query for locations with pagination
  const {
    data: locationsData,
  } = useInfiniteQuery({
    queryKey: ['locations', 'management', selectedRackId],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await warehouseService.getLocations(selectedRackId!, { 
        page: pageParam, 
        limit: 25 
      });
      const items = response?.data?.items || response?.items || [];
      const total = response?.data?.meta?.total ?? response?.meta?.total ?? items.length;
      return { items, total, page: pageParam };
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return totalFetched < lastPage.total ? allPages.length + 1 : undefined;
    },
    enabled: !!selectedRackId,
    initialPageParam: 1,
    staleTime: 30000, // Cache for 30 seconds
    gcTime: 60000, // Keep in cache for 1 minute
  });

  const totalLocationsCount = useMemo(() => {
    if (!locationsData || !locationsData.pages || locationsData.pages.length === 0) return 0;
    return locationsData.pages[0].total || 0;
  }, [locationsData]);

  // Rack locations with pallet occupancy (Racks tab → Locations button)
  const {
    data: rackOccupancyResponse,
    isLoading: rackOccupancyLoading,
    isError: rackOccupancyError,
    error: rackOccupancyErrorData,
  } = useQuery({
    queryKey: ['rack-locations-occupancy', selectedRackId],
    queryFn: () => warehouseService.getRackLocationsWithOccupancy(selectedRackId!),
    enabled: !!selectedRackId && activeTab === 'racks',
    staleTime: 30000,
  });

  const rackOccupancyLocations: Array<{
    id: number;
    locationCode: string;
    status: 'occupied' | 'empty';
    occupiedPercentage: number;
    availableSqFt: number | null;
    locationSizeSqFt: number | null;
    clientName: string | null;
    productDescription: string | null;
    batchNo: string | null;
  }> = useMemo(() => {
    const raw = rackOccupancyResponse?.locations ?? rackOccupancyResponse?.data?.locations ?? [];
    const rackSizeSqFt: number | null =
      rackOccupancyResponse?.locationSizeSqft ?? rackOccupancyResponse?.data?.locationSizeSqft ?? null;
    return raw.map((l: any) => ({
      id: l.locationId,
      locationCode: l.locationCode,
      status: l.status,
      occupiedPercentage: l.occupiedPercentage ?? (l.status === 'occupied' ? 100 : 0),
      availableSqFt: l.availableSqFt ?? null,
      locationSizeSqFt: l.locationSizeSqFt ?? rackSizeSqFt,
      clientName: l.occupiedBy?.clientName ?? null,
      productDescription: l.occupiedBy?.productQuantities ?? l.occupiedBy?.productDescription ?? null,
      batchNo: l.occupiedBy?.batchNo ?? null,
    }));
  }, [rackOccupancyResponse]);

  // Clear the search whenever a different rack is selected — a leftover
  // term would otherwise silently hide every row of the newly-picked rack.
  useEffect(() => {
    setRackLocationSearch('');
  }, [selectedRackId]);

  const filteredRackOccupancyLocations = useMemo(() => {
    const term = rackLocationSearch.trim().toLowerCase();
    if (!term) return rackOccupancyLocations;
    return rackOccupancyLocations.filter((l) =>
      [l.locationCode, l.status === 'occupied' ? 'occupied' : 'available', l.clientName, l.productDescription, l.batchNo]
        .some((field) => field?.toLowerCase().includes(term)),
    );
  }, [rackOccupancyLocations, rackLocationSearch]);

  // ── Manage Zone & Rack tab ──
  const [selectedManageZoneId, setSelectedManageZoneId] = useState<number | null>(null);
  const [selectedManageRackId, setSelectedManageRackId] = useState<number | null>(null);

  // ── Merge pallets (within current drill-down) ──
  const [selectedForMerge, setSelectedForMerge] = useState<PalletRecord[]>([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showMergePicker, setShowMergePicker] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<number | null>(null);

  // Only pallets of the same client may be merged together
  const mergeClientId = selectedForMerge[0]?.clientId ?? null;
  const isSelectedForMerge = (id: number) => selectedForMerge.some((p) => p.id === id);
  const canSelectForMerge = (p: PalletRecord) => mergeClientId == null || p.clientId === mergeClientId;
  const toggleMergeSelect = (p: PalletRecord) =>
    setSelectedForMerge((prev) =>
      prev.some((x) => x.id === p.id) ? prev.filter((x) => x.id !== p.id) : [...prev, p],
    );
  const clearMergeSelection = () => {
    setSelectedForMerge([]);
    setMergeTargetId(null);
    setShowMergeModal(false);
    setShowMergePicker(false);
  };

  // ── Move / relocate a pallet ──
  const [relocatingPallet, setRelocatingPallet] = useState<PalletRecord | null>(null);
  const [relocateType, setRelocateType] = useState<'ground' | 'rack'>('ground');
  const [relocateZoneId, setRelocateZoneId] = useState<number | null>(null);
  const [relocateZoneLocationId, setRelocateZoneLocationId] = useState<number | null>(null);
  const [relocateRackId, setRelocateRackId] = useState<number | null>(null);
  const [relocateLocationId, setRelocateLocationId] = useState<number | null>(null);

  const openRelocate = (p: PalletRecord) => {
    setRelocatingPallet(p);
    setRelocateType(p.storageType);
    setRelocateZoneId(p.zoneId);
    setRelocateZoneLocationId(p.zoneLocationId);
    setRelocateRackId(p.rackId);
    setRelocateLocationId(p.locationId);
  };

  // Zone locations (with occupancy) for the relocate modal's optional slot picker
  const { data: relocateZoneLocationsResponse } = useQuery({
    queryKey: ['zone-locations-occupancy', relocateZoneId],
    queryFn: () => warehouseService.getZoneLocationsWithOccupancy(relocateZoneId!),
    enabled: !!relocatingPallet && relocateType === 'ground' && !!relocateZoneId,
    staleTime: 30000,
  });

  const relocateZoneLocations: Array<{ id: number; code: string; status: string }> = useMemo(() => {
    const raw = relocateZoneLocationsResponse?.locations ?? relocateZoneLocationsResponse?.data?.locations ?? [];
    return raw.map((l: any) => ({ id: l.locationId, code: l.locationCode, status: l.status }));
  }, [relocateZoneLocationsResponse]);

  // ── Manage Pallets table search & filters ──
  const [palletSearch, setPalletSearch] = useState('');
  const [palletClientFilter, setPalletClientFilter] = useState('');
  const [palletProductFilter, setPalletProductFilter] = useState('');
  const [palletLocationFilter, setPalletLocationFilter] = useState('');

  const palletFiltersActive = !!(
    palletSearch.trim() || palletClientFilter || palletProductFilter || palletLocationFilter
  );

  const clearPalletFilters = () => {
    setPalletSearch('');
    setPalletClientFilter('');
    setPalletProductFilter('');
    setPalletLocationFilter('');
  };

  const { data: palletsResponse } = useQuery({
    queryKey: ['warehouse-pallets', selectedWarehouseId],
    queryFn: () => warehouseService.getWarehousePallets(selectedWarehouseId!),
    enabled: !!selectedWarehouseId && activeTab === 'manage',
    staleTime: 30000,
    retry: false,
  });

  const { data: zonesOccupancyResponse, isLoading: zonesOccupancyLoading } = useQuery({
    queryKey: ['zones-occupancy-manage', selectedWarehouseId],
    queryFn: () => warehouseService.getAllZonesOccupancy(selectedWarehouseId!),
    enabled: !!selectedWarehouseId && activeTab === 'manage',
    staleTime: 30000,
  });

  const { data: warehouseStructureResponse, isLoading: warehouseStructureLoading } = useQuery({
    queryKey: ['warehouse-structure-manage', selectedWarehouseId],
    queryFn: () => warehouseService.getWarehouseStructure(selectedWarehouseId!),
    enabled: !!selectedWarehouseId && activeTab === 'manage',
    staleTime: 60000,
  });

  const pallets: PalletRecord[] = useMemo(
    () => (Array.isArray(palletsResponse) ? palletsResponse : palletsResponse?.data ?? []),
    [palletsResponse],
  );

  const locationKey = (p: PalletRecord) =>
    p.storageType === 'rack' ? `rack:${p.rackCode ?? ''}` : `zone:${p.zoneCode ?? ''}`;

  const palletClientOptions = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of pallets) m.set(p.clientId, p.clientName);
    return Array.from(m, ([value, label]) => ({ value: String(value), label }));
  }, [pallets]);

  const palletProductOptions = useMemo(() => {
    const s = new Set<string>();
    for (const p of pallets) if (p.productDescription) s.add(p.productDescription);
    return Array.from(s, (v) => ({ value: v, label: v }));
  }, [pallets]);

  const palletLocationOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of pallets) {
      m.set(
        locationKey(p),
        p.storageType === 'rack' ? `Rack ${p.rackCode ?? '—'}` : `Zone ${p.zoneCode ?? '—'}`,
      );
    }
    return Array.from(m, ([value, label]) => ({ value, label }));
  }, [pallets]);

  const filteredPallets = useMemo(() => {
    const q = palletSearch.trim().toLowerCase();
    return pallets.filter((p) => {
      if (palletClientFilter && String(p.clientId) !== palletClientFilter) return false;
      if (palletProductFilter && p.productDescription !== palletProductFilter) return false;
      if (palletLocationFilter && locationKey(p) !== palletLocationFilter) return false;
      if (q) {
        const hay = `${p.clientName} ${p.productDescription} ${p.batchNo} ${p.zoneCode ?? ''} ${p.zoneLocationCode ?? ''} ${p.rackCode ?? ''} ${p.locationCode ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [pallets, palletSearch, palletClientFilter, palletProductFilter, palletLocationFilter]);

  const zonesOccupancy: Array<{ zoneId: number; zoneCode: string; zoneName: string | null; totalPalletsStored: number; totalSqftStored?: number; areaSqft?: number | null }> = useMemo(
    () => (Array.isArray(zonesOccupancyResponse) ? zonesOccupancyResponse : zonesOccupancyResponse?.data ?? []),
    [zonesOccupancyResponse],
  );

  const structureRacks: Array<{ id: number; code: string; name: string | null; status: string; locations: Array<{ id: number; code: string; status: string }> }> = useMemo(
    () => warehouseStructureResponse?.warehouse?.racks ?? warehouseStructureResponse?.data?.warehouse?.racks ?? [],
    [warehouseStructureResponse],
  );

  const managedZones = useMemo(() => {
    const structureZones: Array<{ id: number; code: string; name: string | null; type: string; status: string }> =
      warehouseStructureResponse?.warehouse?.zones ?? warehouseStructureResponse?.data?.warehouse?.zones ?? [];
    const occupancyMap = new Map(zonesOccupancy.map((z) => [z.zoneId, z]));
    // zones-occupancy doesn't return each zone's Pallet Size, so cross-reference
    // the zones list (which has it) instead of assuming the default 25 sq ft.
    const zoneSizeMap = new Map(zones.map((z: any) => [z.id, z.locationSizeSqft]));
    return structureZones.map((sz) => {
      const occ = occupancyMap.get(sz.id);
      const areaSqft = occ?.areaSqft ?? null;
      const palletSize = Number(zoneSizeMap.get(sz.id)) || SQFT_PER_PALLET;
      const capacityPallets = areaSqft != null ? Math.floor(areaSqft / palletSize) : null;
      const totalPalletsStored = occ?.totalPalletsStored ?? 0;
      const utilizationPct =
        capacityPallets && capacityPallets > 0
          ? Math.min(100, Math.round((totalPalletsStored / capacityPallets) * 100))
          : null;
      return {
        id: sz.id,
        code: sz.code,
        name: sz.name,
        zoneType: sz.type,
        status: sz.status,
        totalPalletsStored,
        areaSqft,
        capacityPallets,
        utilizationPct,
      };
    });
  }, [warehouseStructureResponse, zonesOccupancy, zones]);

  const managedRacks = structureRacks;

  const locationToStorageMap = useMemo(() => {
    const map = new Map<number, PalletRecord>();
    for (const p of pallets) {
      if (p.locationId != null) map.set(p.locationId, p);
    }
    return map;
  }, [pallets]);

  const palletsByZone = useMemo(() => {
    const map = new Map<number, PalletRecord[]>();
    for (const p of pallets) {
      if (p.zoneId != null) {
        const arr = map.get(p.zoneId) ?? [];
        arr.push(p);
        map.set(p.zoneId, arr);
      }
    }
    return map;
  }, [pallets]);

  const totalRackLocations = useMemo(
    () => managedRacks.reduce((sum, r) => sum + r.locations.length, 0),
    [managedRacks],
  );

  const occupiedRackLocations = useMemo(
    () => managedRacks.reduce(
      (sum, r) => sum + r.locations.filter((l) => l.status !== 'available' || locationToStorageMap.has(l.id)).length,
      0,
    ),
    [managedRacks, locationToStorageMap],
  );

  const racksUtilizationPct = useMemo(
    () => (totalRackLocations > 0 ? Math.round((occupiedRackLocations / totalRackLocations) * 100) : 0),
    [occupiedRackLocations, totalRackLocations],
  );

  const zonesUtilization = useMemo(() => {
    const capacity = managedZones.reduce((sum, z) => sum + (z.capacityPallets ?? 0), 0);
    const stored = managedZones.reduce((sum, z) => sum + z.totalPalletsStored, 0);
    return {
      capacity,
      stored,
      pct: capacity > 0 ? Math.min(100, Math.round((stored / capacity) * 100)) : null,
    };
  }, [managedZones]);

  const manageLoading = zonesOccupancyLoading || warehouseStructureLoading;

  const selectedZone = useMemo(
    () => managedZones.find((z) => z.id === selectedManageZoneId) ?? null,
    [managedZones, selectedManageZoneId],
  );
  const selectedZoneProducts = useMemo(
    () => (selectedManageZoneId != null ? (palletsByZone.get(selectedManageZoneId) ?? []) : []),
    [palletsByZone, selectedManageZoneId],
  );
  const selectedRackManage = useMemo(
    () => managedRacks.find((r) => r.id === selectedManageRackId) ?? null,
    [managedRacks, selectedManageRackId],
  );

  const createWarehouseMutation = useMutation({
    mutationFn: async () => {
      const created = await warehouseService.createWarehouse({
        name: warehouseForm.name.trim(),
        code: warehouseForm.code.trim().toUpperCase(),
        address: warehouseForm.address.trim(),
        totalSpaceSqft: Number(warehouseForm.totalSpaceSqft),
      });
      // Billing details are a separate (PATCH) DTO on the backend, so persist
      // whatever the user filled in on the create form as a follow-up update —
      // same endpoint the "Company & Billing Details" edit section already uses.
      const billingPayload = {
        companyName: warehouseForm.companyName.trim(),
        companyGstin: warehouseForm.companyGstin.trim(),
        companyPan: warehouseForm.companyPan.trim(),
        bankName: warehouseForm.bankName.trim(),
        bankAccountNo: warehouseForm.bankAccountNo.trim(),
        bankIfsc: warehouseForm.bankIfsc.trim(),
        bankBranch: warehouseForm.bankBranch.trim(),
        companyTagline: warehouseForm.companyTagline.trim(),
        companyEmail: warehouseForm.companyEmail.trim(),
        companyJurisdiction: warehouseForm.companyJurisdiction.trim(),
      };
      if (created?.id && Object.values(billingPayload).some((value) => value !== '')) {
        await warehouseService.updateWarehouse(created.id, billingPayload);
      }
      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses', 'management'] });
      setWarehouseForm({
        name: '',
        code: '',
        address: '',
        totalSpaceSqft: '',
        companyName: '',
        companyGstin: '',
        companyPan: '',
        bankName: '',
        bankAccountNo: '',
        bankIfsc: '',
        bankBranch: '',
        companyTagline: '',
        companyEmail: '',
        companyJurisdiction: '',
      });
      setWarehouseBillingErrors({});
      setShowCreateModal(false);
      toast.success('Warehouse created successfully');
    },
    onError: (error: any) => {
      const errorMessage = getErrorMessage(error, 'Failed to create warehouse');
      // Show the exact error message from backend (includes "Duplicate Warehouse Location Code detected")
      toast.error(errorMessage);
    },
  });

  const updateWarehouseCodeMutation = useMutation({
    mutationFn: ({ warehouseId, code }: { warehouseId: number; code: string }) =>
      warehouseService.updateWarehouse(warehouseId, { code }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses', 'management'] });
      setIsEditingCode(false);
      toast.success('Location code updated successfully');
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update location code'));
    },
  });

  const updateWarehouseBillingDetailsMutation = useMutation({
    mutationFn: ({ warehouseId, payload }: { warehouseId: number; payload: typeof billingDetailsForm }) =>
      warehouseService.updateWarehouse(warehouseId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses', 'management'] });
      setIsEditingBillingDetails(false);
      toast.success('Billing details updated successfully');
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update billing details'));
    },
  });

  const createZoneMutation = useMutation({
    mutationFn: async () => {
      const zone = await warehouseService.createZone({
        warehouseId: selectedWarehouseId!,
        code: zoneForm.code.trim().toUpperCase(),
        name: zoneForm.name.trim(),
        zoneType: zoneForm.zoneType.trim(),
        status: zoneForm.status,
        areaSqft: zoneForm.areaSqft ? Number(zoneForm.areaSqft) : undefined,
        locationSizeSqft: zoneForm.locationSizeSqft ? Number(zoneForm.locationSizeSqft) : undefined,
      });
      // Area Allocation is enforced (on submit) to be an exact multiple of
      // Pallet Size, so this always divides evenly — auto-generate exactly
      // that many location slots instead of leaving the zone with none.
      const areaSqft = Number(zoneForm.areaSqft);
      const palletSize = Number(zoneForm.locationSizeSqft);
      if (zone?.id && areaSqft > 0 && palletSize > 0) {
        const locationCount = Math.floor(areaSqft / palletSize);
        if (locationCount > 0) {
          await warehouseService.generateZoneLocations(zone.id, { locations: locationCount });
        }
      }
      return zone;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones', 'management', selectedWarehouseId] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-utilization', selectedWarehouseId] });
      queryClient.invalidateQueries({ queryKey: ['zone-locations', 'management'] });
      queryClient.invalidateQueries({ queryKey: ['zone-locations-occupancy'] });
      setZoneForm({ code: '', name: '', zoneType: 'regular', status: 'active', areaSqft: '', locationSizeSqft: '' });
      toast.success('Zone created and locations generated successfully');
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to create zone'));
    },
  });

  const updateZoneMutation = useMutation({
    mutationFn: (payload: { id: number; data: { code?: string; name?: string; zoneType?: string; status?: string; areaSqft?: number; locationSizeSqft?: number | null } }) =>
      warehouseService.updateZone(payload.id, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones', 'management', selectedWarehouseId] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-utilization', selectedWarehouseId] });
      queryClient.invalidateQueries({ queryKey: ['zone-locations', 'management'] });
      queryClient.invalidateQueries({ queryKey: ['zone-locations-occupancy'] });
      toast.success('Zone updated successfully');
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update zone'));
    },
  });

  const createRackMutation = useMutation({
    mutationFn: async () => {
      const rack = await warehouseService.createRack({
        warehouseId: selectedWarehouseId!,
        code: rackForm.code.trim().toUpperCase(),
        name: rackForm.name.trim(),
        rackType: rackForm.rackType,
        status: rackForm.status,
        locationSizeSqft: rackForm.locationSizeSqft ? Number(rackForm.locationSizeSqft) : null,
        rackCount: rackForm.rackCount ? Number(rackForm.rackCount) : undefined,
      });
      const rackCount = Number(rackForm.rackCount);
      if (rack?.id && rackCount > 0) {
        await warehouseService.generateLocations(rack.id, { locations: rackCount });
      }
      return rack;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['racks', 'management', selectedWarehouseId] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-utilization', selectedWarehouseId] });
      queryClient.invalidateQueries({ queryKey: ['locations', 'management'] });
      queryClient.invalidateQueries({ queryKey: ['rack-locations-occupancy'] });
      setRackForm({ code: '', name: '', rackType: 'regular', status: 'active', locationSizeSqft: '', rackCount: '' });
      toast.success('Rack created and locations generated successfully');
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to create rack'));
    },
  });

  const updateRackMutation = useMutation({
    mutationFn: (payload: { id: number; data: { code?: string; name?: string; rackType?: string; status?: string; locationSizeSqft?: number | null; rackCount?: number } }) =>
      warehouseService.updateRack(payload.id, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['racks', 'management', selectedWarehouseId] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-utilization', selectedWarehouseId] });
      queryClient.invalidateQueries({ queryKey: ['locations', 'management'] });
      queryClient.invalidateQueries({ queryKey: ['rack-locations-occupancy'] });
      toast.success('Rack updated successfully');
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update rack'));
    },
  });

  // Manual top-up of locations for an already-existing rack (separate from the
  // automatic generation that happens at rack-creation time above).
  const generateLocationsMutation = useMutation({
    mutationFn: () =>
      warehouseService.generateLocations(selectedRackId!, {
        locations: Number(locationsForm.count),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations', 'management', selectedRackId] });
      queryClient.invalidateQueries({ queryKey: ['rack-locations-occupancy', selectedRackId] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-utilization', selectedWarehouseId] });
      setLocationsForm({ count: '' });
      toast.success('Locations updated successfully');
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to generate locations'));
    },
  });

  const handleGenerateLocations = () => {
    if (!selectedRackId) {
      toast.error('Select a rack first');
      return;
    }
    if (!locationsForm.count || Number(locationsForm.count) <= 0) {
      toast.error('Enter a valid target locations count');
      return;
    }
    generateLocationsMutation.mutate();
  };

  // ── Optimistic cache helpers ──
  const invalidateManageQueries = useCallback(
    (warehouseId: number | null) => {
      queryClient.invalidateQueries({ queryKey: ['warehouse-pallets', warehouseId] });
      queryClient.invalidateQueries({ queryKey: ['zones-occupancy-manage', warehouseId] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-structure-manage', warehouseId] });
    },
    [queryClient],
  );

  const applyOptimisticMerge = (
    warehouseId: number | null,
    payload: { targetStorageId: number; sourceStorageIds: number[] },
  ) => {
    queryClient.setQueryData(['warehouse-pallets', warehouseId], (old: any) => {
      if (!Array.isArray(old)) return old;
      const sources = new Set(payload.sourceStorageIds);
      const added = old
        .filter((p: PalletRecord) => sources.has(p.id))
        .reduce((s: number, p: PalletRecord) => s + p.palletCount, 0);
      return old
        .filter((p: PalletRecord) => !sources.has(p.id))
        .map((p: PalletRecord) =>
          p.id === payload.targetStorageId ? { ...p, palletCount: p.palletCount + added } : p,
        );
    });
  };

  const applyOptimisticRelocate = (
    old: any,
    payload: { storageId: number; data: { storageType: 'ground' | 'rack'; zoneId?: number; zoneLocationId?: number; rackId?: number; locationId?: number } },
  ) => {
    if (!Array.isArray(old)) return old;
    return old.map((p: PalletRecord) => {
      if (p.id !== payload.storageId) return p;
      if (payload.data.storageType === 'ground') {
        const zone = managedZones.find((z) => z.id === payload.data.zoneId);
        const zoneLoc = relocateZoneLocations.find((l) => l.id === payload.data.zoneLocationId);
        return { ...p, storageType: 'ground' as const, zoneId: payload.data.zoneId ?? null, zoneCode: zone?.code ?? null, zoneLocationId: payload.data.zoneLocationId ?? null, zoneLocationCode: zoneLoc?.code ?? null, rackId: null, rackCode: null, locationId: null, locationCode: null };
      }
      const rack = managedRacks.find((r) => r.id === payload.data.rackId);
      const loc = rack?.locations.find((l) => l.id === payload.data.locationId);
      return { ...p, storageType: 'rack' as const, zoneId: null, zoneCode: null, zoneLocationId: null, zoneLocationCode: null, rackId: payload.data.rackId ?? null, rackCode: rack?.code ?? null, locationId: payload.data.locationId ?? null, locationCode: loc?.code ?? null };
    });
  };

  // ── Merge (deferred a few seconds so it can be undone) ──
  const mergePalletsMutation = useMutation({
    mutationFn: (vars: { payload: { targetStorageId: number; sourceStorageIds: number[] }; snapshot: unknown; warehouseId: number | null }) =>
      warehouseService.mergePallets(vars.payload),
    onError: (error: any, vars) => {
      queryClient.setQueryData(['warehouse-pallets', vars.warehouseId], vars.snapshot);
      toast.error(getErrorMessage(error, 'Failed to merge pallets'));
    },
    onSuccess: () => {
      toast.success('Pallets merged successfully');
    },
    onSettled: (_d, _e, vars) => {
      invalidateManageQueries(vars.warehouseId);
    },
  });

  const [pendingMerge, setPendingMerge] = useState<{ count: number; total: number } | null>(null);
  const pendingMergeRef = useRef<{
    timeoutId: ReturnType<typeof setTimeout>;
    payload: { targetStorageId: number; sourceStorageIds: number[] };
    snapshot: unknown;
    warehouseId: number | null;
  } | null>(null);
  const MERGE_UNDO_MS = 5000;

  const commitPendingMerge = useCallback(() => {
    const pend = pendingMergeRef.current;
    if (!pend) return;
    clearTimeout(pend.timeoutId);
    pendingMergeRef.current = null;
    setPendingMerge(null);
    mergePalletsMutation.mutate({ payload: pend.payload, snapshot: pend.snapshot, warehouseId: pend.warehouseId });
  }, [mergePalletsMutation]);

  const undoPendingMerge = () => {
    const pend = pendingMergeRef.current;
    if (!pend) return;
    clearTimeout(pend.timeoutId);
    queryClient.setQueryData(['warehouse-pallets', pend.warehouseId], pend.snapshot);
    pendingMergeRef.current = null;
    setPendingMerge(null);
    toast.info('Merge cancelled');
  };

  // On unmount, flush any pending merge so it is not silently lost.
  useEffect(() => {
    return () => {
      const pend = pendingMergeRef.current;
      if (pend) {
        clearTimeout(pend.timeoutId);
        warehouseService.mergePallets(pend.payload).catch((error: any) => {
          toast.error(getErrorMessage(error, 'Failed to merge pallets'));
        });
        pendingMergeRef.current = null;
      }
    };
  }, []);

  // ── Move / relocate (optimistic) ──
  const relocatePalletMutation = useMutation({
    mutationFn: (payload: {
      storageId: number;
      data: { storageType: 'ground' | 'rack'; zoneId?: number; zoneLocationId?: number; rackId?: number; locationId?: number };
    }) => warehouseService.relocatePallet(payload.storageId, payload.data),
    onMutate: async (payload) => {
      const warehouseId = selectedWarehouseId;
      await queryClient.cancelQueries({ queryKey: ['warehouse-pallets', warehouseId] });
      const snapshot = queryClient.getQueryData(['warehouse-pallets', warehouseId]);
      queryClient.setQueryData(['warehouse-pallets', warehouseId], (old: any) => applyOptimisticRelocate(old, payload));
      return { snapshot, warehouseId };
    },
    onError: (error: any, _payload, context) => {
      if (context) queryClient.setQueryData(['warehouse-pallets', context.warehouseId], context.snapshot);
      toast.error(getErrorMessage(error, 'Failed to move pallet'));
    },
    onSuccess: () => {
      setRelocatingPallet(null);
      toast.success('Pallet moved successfully');
    },
    onSettled: (_d, _e, _payload, context) => {
      invalidateManageQueries(context?.warehouseId ?? selectedWarehouseId);
    },
  });

  const handleConfirmRelocate = () => {
    if (!relocatingPallet) return;
    if (relocateType === 'ground') {
      if (!relocateZoneId) {
        toast.error('Select a destination zone');
        return;
      }
      relocatePalletMutation.mutate({
        storageId: relocatingPallet.id,
        data: { storageType: 'ground', zoneId: relocateZoneId, zoneLocationId: relocateZoneLocationId ?? undefined },
      });
    } else {
      if (!relocateRackId || !relocateLocationId) {
        toast.error('Select a destination rack and location');
        return;
      }
      relocatePalletMutation.mutate({
        storageId: relocatingPallet.id,
        data: { storageType: 'rack', rackId: relocateRackId, locationId: relocateLocationId },
      });
    }
  };

  const handleConfirmMerge = () => {
    if (mergeTargetId == null) {
      toast.error('Select which pallet to keep');
      return;
    }
    const sourceStorageIds = selectedForMerge
      .filter((p) => p.id !== mergeTargetId)
      .map((p) => p.id);
    if (sourceStorageIds.length === 0) {
      toast.error('Select at least two pallets to merge');
      return;
    }

    // Flush any prior pending merge before queuing a new one.
    commitPendingMerge();

    const warehouseId = selectedWarehouseId;
    const payload = { targetStorageId: mergeTargetId, sourceStorageIds };
    const total = selectedForMerge.reduce((s, p) => s + p.palletCount, 0);
    const snapshot = queryClient.getQueryData(['warehouse-pallets', warehouseId]);

    applyOptimisticMerge(warehouseId, payload);
    setShowMergeModal(false);
    setSelectedForMerge([]);
    setMergeTargetId(null);

    const timeoutId = setTimeout(() => commitPendingMerge(), MERGE_UNDO_MS);
    pendingMergeRef.current = { timeoutId, payload, snapshot, warehouseId };
    setPendingMerge({ count: sourceStorageIds.length, total });
  };

  const handleCreateWarehouse = () => {
    if (!warehouseForm.name.trim()) {
      toast.error('Warehouse name is required');
      return;
    }
    if (!warehouseForm.code.trim()) {
      toast.error('Location code is required');
      return;
    }
    if (!/^[A-Z0-9_-]{1,5}$/.test(warehouseForm.code.trim().toUpperCase())) {
      toast.error('Location code must be 1-5 characters: letters, numbers, hyphen or underscore');
      return;
    }
    if (!warehouseForm.address.trim()) {
      toast.error('Warehouse address is required');
      return;
    }
    if (warehouseForm.address.trim().length > 300) {
      toast.error('Warehouse address must not exceed 300 characters');
      return;
    }
    if (!/^[a-zA-Z0-9\s,.\-/()#]+$/.test(warehouseForm.address.trim())) {
      toast.error('Warehouse address can only contain alphanumeric characters and basic punctuation (comma, period, hyphen, slash, parentheses, hash)');
      return;
    }
    if (!warehouseForm.totalSpaceSqft || Number(warehouseForm.totalSpaceSqft) < SQFT_PER_PALLET) {
      toast.error(`Total space must be at least ${SQFT_PER_PALLET} sqft (the size of one pallet)`);
      return;
    }
    const billingErrors = computeBillingErrors(warehouseForm);
    setWarehouseBillingErrors(billingErrors);
    if (Object.keys(billingErrors).length > 0) {
      toast.error('Please fix the highlighted billing fields');
      return;
    }
    createWarehouseMutation.mutate();
  };

  // All billing fields are optional (a warehouse may not have GST/bank details
  // configured yet), but whatever is entered must be a well-formed Indian
  // GSTIN/PAN/IFSC/bank-account/email — not free-form text. Shared by both the
  // create form and the edit section so the rules stay in one place.
  const computeBillingErrors = (form: {
    companyName: string;
    companyGstin: string;
    companyPan: string;
    bankName: string;
    bankAccountNo: string;
    bankIfsc: string;
    bankBranch: string;
    companyTagline: string;
    companyEmail: string;
    companyJurisdiction: string;
  }): Record<string, string> => {
    const errors: Record<string, string> = {};

    const gstinError = FIELD_VALIDATORS.gstNumber(form.companyGstin, false);
    if (gstinError) errors.companyGstin = gstinError;

    const panError = FIELD_VALIDATORS.panNumber(form.companyPan, false);
    if (panError) errors.companyPan = panError;

    const ifscError = FIELD_VALIDATORS.ifscCode(form.bankIfsc, false);
    if (ifscError) errors.bankIfsc = ifscError;

    const emailError = FIELD_VALIDATORS.email(form.companyEmail, false);
    if (emailError) errors.companyEmail = emailError;

    const jurisdictionError = FIELD_VALIDATORS.cityName(form.companyJurisdiction, false);
    if (jurisdictionError) errors.companyJurisdiction = jurisdictionError;

    const accountError = validateField(
      'Account Number',
      form.bankAccountNo,
      VALIDATION_PATTERNS.BANK_ACCOUNT_NUMBER,
      undefined,
      undefined,
      false,
    );
    if (accountError) errors.bankAccountNo = accountError;

    const companyNameError = validateField(
      'Company Name',
      form.companyName,
      VALIDATION_PATTERNS.LEGAL_ENTITY_NAME,
      undefined,
      255,
      false,
    );
    if (companyNameError) errors.companyName = companyNameError;

    const bankNameError = validateField(
      'Bank Name',
      form.bankName,
      VALIDATION_PATTERNS.LEGAL_ENTITY_NAME,
      undefined,
      255,
      false,
    );
    if (bankNameError) errors.bankName = bankNameError;

    const branchError = validateField(
      'Branch',
      form.bankBranch,
      VALIDATION_PATTERNS.LEGAL_ENTITY_NAME,
      undefined,
      255,
      false,
    );
    if (branchError) errors.bankBranch = branchError;

    const taglineError = validateField(
      'Tagline',
      form.companyTagline,
      VALIDATION_PATTERNS.LEGAL_ENTITY_NAME,
      undefined,
      150,
      false,
    );
    if (taglineError) errors.companyTagline = taglineError;

    return errors;
  };

  const validateBillingDetails = (): boolean => {
    const errors = computeBillingErrors(billingDetailsForm);
    setBillingDetailsErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveBillingDetails = () => {
    if (!selectedWarehouse) return;
    if (!validateBillingDetails()) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    updateWarehouseBillingDetailsMutation.mutate({ warehouseId: selectedWarehouse.id, payload: billingDetailsForm });
  };

  const handleCreateZone = () => {
    if (!selectedWarehouseId) {
      toast.error('Select a warehouse first');
      return;
    }
    if (!zoneForm.code.trim()) {
      toast.error('Zone code is required');
      return;
    }
    if (!zoneForm.name.trim()) {
      toast.error('Zone name is required');
      return;
    }
    if (!zoneForm.zoneType.trim()) {
      toast.error('Zone type is required');
      return;
    }
    if (!zoneForm.areaSqft || Number(zoneForm.areaSqft) <= 0) {
      toast.error('Area allocation is required');
      return;
    }
    if (!zoneForm.status.trim()) {
      toast.error('Status is required');
      return;
    }
    if (!zoneForm.locationSizeSqft || Number(zoneForm.locationSizeSqft) <= 0) {
      toast.error('Location size is required');
      return;
    }
    if (Number(zoneForm.locationSizeSqft) < MIN_PALLET_SIZE_SQFT) {
      toast.error(`Pallet Size must be at least ${MIN_PALLET_SIZE_SQFT} sq ft`);
      return;
    }
    if (Number(zoneForm.areaSqft) % Number(zoneForm.locationSizeSqft) !== 0) {
      toast.error(`Area Allocation must be an exact multiple of Pallet Size (${zoneForm.locationSizeSqft} sq ft)`);
      return;
    }
    if (Number(zoneForm.areaSqft) / Number(zoneForm.locationSizeSqft) > MAX_ZONE_PALLETS) {
      toast.error(`A zone cannot exceed ${MAX_ZONE_PALLETS} pallets. Reduce the area or increase the pallet size.`);
      return;
    }
    if (
      zoneForm.areaSqft &&
      Number(zoneForm.areaSqft) > 0 &&
      utilization &&
      Number(zoneForm.areaSqft) > utilization.availableSqft + 0.001
    ) {
      toast.error(
        `Only ${utilization.availableSqft.toLocaleString()} sqft available in this warehouse ` +
          `(total ${utilization.totalSpaceSqft.toLocaleString()} sqft, ` +
          `${utilization.assignedSqft.toLocaleString()} already assigned).`,
      );
      return;
    }
    createZoneMutation.mutate();
  };

  const handleCreateRack = () => {
    if (!selectedWarehouseId) {
      toast.error('Select a warehouse first');
      return;
    }
    if (!rackForm.code.trim()) {
      toast.error('Rack code is required');
      return;
    }
    if (!rackForm.name.trim()) {
      toast.error('Rack name is required');
      return;
    }
    if (!rackForm.rackType.trim()) {
      toast.error('Rack type is required');
      return;
    }
    if (!rackForm.status.trim()) {
      toast.error('Status is required');
      return;
    }
    if (!rackForm.locationSizeSqft || Number(rackForm.locationSizeSqft) <= 0) {
      toast.error('Pallet size is required');
      return;
    }
    if (Number(rackForm.locationSizeSqft) < MIN_PALLET_SIZE_SQFT) {
      toast.error(`Pallet Size must be at least ${MIN_PALLET_SIZE_SQFT} sq ft`);
      return;
    }
    if (!rackForm.rackCount || !Number.isInteger(Number(rackForm.rackCount)) || Number(rackForm.rackCount) <= 0) {
      toast.error('Rack count is required and must be a whole number');
      return;
    }
    if (Number(rackForm.rackCount) > MAX_RACK_PALLETS) {
      toast.error(`Rack Count cannot exceed ${MAX_RACK_PALLETS} pallet slots.`);
      return;
    }
    const rackAreaSqft = Number(rackForm.rackCount) * Number(rackForm.locationSizeSqft);
    if (utilization && rackAreaSqft > utilization.availableSqft + 0.001) {
      toast.error(
        `Only ${utilization.availableSqft.toLocaleString()} sqft available in this warehouse ` +
          `(total ${utilization.totalSpaceSqft.toLocaleString()} sqft, ` +
          `${utilization.assignedSqft.toLocaleString()} already assigned). ` +
          `This rack needs ${rackAreaSqft.toLocaleString()} sqft (${rackForm.rackCount} × ${rackForm.locationSizeSqft} sqft).`,
      );
      return;
    }
    createRackMutation.mutate();
  };


  const handleLoadMoreZones = useCallback(() => {
    if (hasNextZonesPage && !isFetchingNextZonesPage) {
      fetchNextZonesPage();
    }
  }, [hasNextZonesPage, isFetchingNextZonesPage, fetchNextZonesPage]);

  const zoneTable = (
    <ResponsiveTable
      columns={[
        {
          key: 'code',
          label: 'Zone Code',
          width: '150px',
          render: (_, row) => <div className="font-medium text-gray-900">{row.code}</div>,
        },
        {
          key: 'name',
          label: 'Zone Name',
          width: '200px',
          render: (_, row) => (
            <div className="text-gray-900">{row.name || <span className="text-gray-400 italic">Not set</span>}</div>
          ),
        },
        {
          key: 'zoneType',
          label: 'Zone Type',
          width: '180px',
          render: (_, row) => (
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
              row.zoneType === 'heavy'
                ? 'bg-orange-100 text-orange-800'
                : row.zoneType === 'lbd'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-800'
            }`}>
              {row.zoneType === 'heavy'
                ? 'Heavy'
                : row.zoneType === 'lbd'
                  ? 'LBD'
                  : row.zoneType === 'regular'
                    ? 'Regular'
                    : (row.zoneType || 'Regular')}
            </span>
          ),
        },
        {
          key: 'status',
          label: 'Status',
          width: '120px',
          render: (_, row) => (
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
              row.status === 'active'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {row.status}
            </span>
          ),
        },
        {
          key: 'areaSqft',
          label: 'Area Allocation',
          width: '200px',
          render: (_, row) =>
            row.areaSqft != null ? (
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-gray-900">{row.areaSqft.toLocaleString()} sqft</span>
                <span className="text-xs text-brand-600 font-medium">{Math.floor(row.areaSqft / (Number(row.locationSizeSqft) || SQFT_PER_PALLET))} pallets</span>
              </div>
            ) : (
              <span className="text-gray-400 italic text-sm">Not set</span>
            ),
        },
        {
          key: 'locationSizeSqft',
          label: 'Pallet Size (sq ft)',
          width: '150px',
          render: (_, row) => (
            <div className="text-gray-900">
              {row.locationSizeSqft != null
                ? <span className="font-medium">{row.locationSizeSqft} sq ft</span>
                : <span className="text-gray-400 italic">Not set</span>}
            </div>
          ),
        },
        {
          key: 'actions',
          label: 'Actions',
          width: '220px',
          render: (_, row) => (
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => {
                  updateZoneMutation.mutate({
                    id: row.id,
                    data: { status: row.status === 'active' ? 'inactive' : 'active' },
                  });
                }}
                className={`px-3 py-1.5 text-white text-sm font-medium rounded-lg transition-colors inline-flex items-center ${
                  row.status === 'active'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                title={row.status === 'active' ? 'Deactivate this zone' : 'Activate this zone'}
              >
                {row.status === 'active' ? 'Inactive' : 'Active'}
              </button>
              <button
                onClick={() => {
                  setSelectedZoneId(row.id);
                  // Take the user down to the Zone Location Management list for this zone.
                  requestAnimationFrame(() =>
                    zoneLocationSectionRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    }),
                  );
                }}
                className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors inline-flex items-center"
              >
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Locations
              </button>
            </div>
          ),
        },
      ]}
      data={zones}
      isLoading={isFetchingNextZonesPage}
      onLoadMore={handleLoadMoreZones}
      hasMore={hasNextZonesPage}
      height="500px"
      emptyMessage="No zones created yet for this warehouse. Create your first zone above."
    />
  );

  const handleLoadMoreRacks = useCallback(() => {
    if (hasNextRacksPage && !isFetchingNextRacksPage) {
      fetchNextRacksPage();
    }
  }, [hasNextRacksPage, isFetchingNextRacksPage, fetchNextRacksPage]);

  const rackTable = (
    <ResponsiveTable
      columns={[
        {
          key: 'code',
          label: 'Rack Code',
          width: '150px',
          render: (_, row) => <div className="font-medium text-gray-900">{row.code}</div>,
        },
        {
          key: 'name',
          label: 'Rack Name',
          width: '200px',
          render: (_, row) => (
            <div className="text-gray-900">{row.name || <span className="text-gray-400 italic">Not set</span>}</div>
          ),
        },
        {
          key: 'rackType',
          label: 'Rack Type',
          width: '160px',
          render: (_, row) => (
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full capitalize ${
              row.rackType === 'heavy'
                ? 'bg-orange-100 text-orange-800'
                : row.rackType === 'lbd'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-800'
            }`}>
              {row.rackType === 'heavy'
                ? 'Heavy'
                : row.rackType === 'lbd'
                  ? 'LBD'
                  : row.rackType === 'regular'
                    ? 'Regular'
                    : (row.rackType || 'Regular')}
            </span>
          ),
        },
        {
          key: 'status',
          label: 'Status',
          width: '120px',
          render: (_, row) => (
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
              row.status === 'active'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {row.status}
            </span>
          ),
        },
        {
          key: 'locationSizeSqft',
          label: 'Pallet Size (sq ft)',
          width: '150px',
          render: (_, row) => (
            <div className="text-gray-900">
              {row.locationSizeSqft != null
                ? <span className="font-medium">{row.locationSizeSqft} sq ft</span>
                : <span className="text-gray-400 italic">Not set</span>}
            </div>
          ),
        },
        {
          key: 'rackCount',
          label: 'Pallet Slots',
          width: '150px',
          render: (_, row) => (
            <div className="text-gray-900">
              {row.rackCount != null
                ? <span className="font-medium">{row.rackCount}</span>
                : <span className="text-gray-400 italic">Not set</span>}
            </div>
          ),
        },
        {
          key: 'actions',
          label: 'Actions',
          width: '220px',
          render: (_, row) => (
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => {
                  updateRackMutation.mutate({
                    id: row.id,
                    data: { status: row.status === 'active' ? 'inactive' : 'active' },
                  });
                }}
                className={`px-3 py-1.5 text-white text-sm font-medium rounded-lg transition-colors flex items-center ${
                  row.status === 'active'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                title={row.status === 'active' ? 'Deactivate this rack' : 'Activate this rack'}
              >
                {row.status === 'active' ? 'Inactive' : 'Active'}
              </button>
              <button
                onClick={() => {
                  setSelectedRackId(row.id);
                  // Take the user down to the Location Management list for this rack.
                  requestAnimationFrame(() =>
                    locationSectionRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    }),
                  );
                }}
                className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center"
              >
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Locations
              </button>
            </div>
          ),
        },
      ]}
      data={racks}
      isLoading={isFetchingNextRacksPage}
      onLoadMore={handleLoadMoreRacks}
      hasMore={hasNextRacksPage}
      height="500px"
      emptyMessage="No racks created yet for this warehouse. Create your first rack above."
    />
  );

  const mergeToolbar = selectedForMerge.length > 0 && (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-emerald-50 border-2 border-emerald-200 rounded-xl px-4 py-3 mb-3">
      <div className="text-sm text-emerald-800">
        <span className="font-bold">{selectedForMerge.length}</span> pallet{selectedForMerge.length === 1 ? '' : 's'} selected
        {mergeClientId != null && <span className="font-medium"> · {selectedForMerge[0].clientName}</span>}
        <span className="block text-xs text-emerald-600 mt-0.5">Only pallets of the same client can be merged together.</span>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <button
          onClick={() => setSelectedForMerge([])}
          className="px-4 py-2 bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg transition-colors"
        >
          Clear
        </button>
        <button
          onClick={() => { setMergeTargetId(selectedForMerge[0].id); setShowMergeModal(true); }}
          disabled={selectedForMerge.length < 2}
          className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white text-sm font-semibold rounded-lg transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
          Merge Pallets
        </button>
      </div>
    </div>
  );

  // Shared by the manage-tab desktop tables and their mobile card lists
  const managedZoneCardColumns: TableColumn[] = [
    {
      key: 'code',
      label: 'Zone',
      render: (_: any, zone: any) => (
        <div>
          <div className="font-bold text-gray-900">{zone.code}</div>
          {zone.name && <div className="text-xs text-gray-500 font-normal">{zone.name}</div>}
        </div>
      ),
    },
    {
      key: 'zoneType',
      label: 'Type',
      render: (value: any) => (
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">{value}</span>
      ),
    },
    {
      key: 'availability',
      label: 'Availability',
      render: (_: any, zone: any) => {
        const isOccupied = zone.totalPalletsStored > 0;
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${isOccupied ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isOccupied ? 'bg-amber-500' : 'bg-green-500'}`} />
            {isOccupied ? 'In Use' : 'Available'}
          </span>
        );
      },
    },
    {
      key: 'totalPalletsStored',
      label: 'Pallets Stored',
      render: (_: any, zone: any) => {
        const isOccupied = zone.totalPalletsStored > 0;
        return zone.capacityPallets != null
          ? <span className={isOccupied ? 'font-semibold text-amber-700' : 'text-green-600 font-medium'}>{zone.totalPalletsStored} / {zone.capacityPallets} pallets</span>
          : isOccupied
            ? <span className="font-semibold text-amber-700">{zone.totalPalletsStored} pallets</span>
            : <span className="text-green-600 font-medium">0 — Free</span>;
      },
    },
    {
      key: 'utilizationPct',
      label: 'Utilization',
      render: (value: any) =>
        value != null ? (
          <div className="flex items-center gap-2 w-36">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${value > 85 ? 'bg-red-500' : value > 60 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${value}%` }} />
            </div>
            <span className="text-xs text-gray-600 font-medium">{value}%</span>
          </div>
        ) : (
          <span className="text-xs text-gray-400 italic">No capacity set</span>
        ),
    },
    {
      key: 'actions',
      label: 'View',
      render: (_: any, zone: any) => (
        <button
          onClick={() => { clearMergeSelection(); setSelectedManageZoneId(zone.id); }}
          className="w-full min-h-[42px] inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-xs font-semibold transition-colors border border-amber-200 active:scale-[0.98]"
        >
          View <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      ),
    },
  ];

  const managedRackCardColumns: TableColumn[] = [
    {
      key: 'code',
      label: 'Rack',
      render: (_: any, rack: any) => (
        <div>
          <div className="font-bold text-gray-900">{rack.code}</div>
          {rack.name && <div className="text-xs text-gray-500 font-normal">{rack.name}</div>}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: any) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${value === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>{value}</span>
      ),
    },
    {
      key: 'locations',
      label: 'Locations',
      render: (_: any, rack: any) => {
        const totalLocs = rack.locations.length;
        const occupiedLocs = rack.locations.filter((l: any) => l.status !== 'available' || locationToStorageMap.has(l.id)).length;
        return (
          <span>
            <span className="font-semibold text-amber-700">{occupiedLocs}</span> occupied · <span className="font-semibold text-green-700">{totalLocs - occupiedLocs}</span> free · {totalLocs} total
          </span>
        );
      },
    },
    {
      key: 'utilization',
      label: 'Utilization',
      render: (_: any, rack: any) => {
        const totalLocs = rack.locations.length;
        const occupiedLocs = rack.locations.filter((l: any) => l.status !== 'available' || locationToStorageMap.has(l.id)).length;
        const utilPct = totalLocs > 0 ? Math.round((occupiedLocs / totalLocs) * 100) : 0;
        return (
          <div className="flex items-center gap-2 w-36">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${utilPct > 85 ? 'bg-red-500' : utilPct > 60 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${utilPct}%` }} />
            </div>
            <span className="text-xs text-gray-600 font-medium">{utilPct}%</span>
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: 'View',
      render: (_: any, rack: any) => (
        <button
          onClick={() => { clearMergeSelection(); setSelectedManageRackId(rack.id); }}
          className="w-full min-h-[42px] inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-brand-50 hover:bg-brand-100 text-brand-700 rounded-lg text-xs font-semibold transition-colors border border-brand-200 active:scale-[0.98]"
        >
          View <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      ),
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen px-3 py-4 lg:p-4 bg-gradient-to-br from-gray-50 to-gray-100 scrollbar-hide overflow-x-hidden" style={{ overflowY: 'auto' }}>
        {/* Header with enhanced shadow and blue tint */}
        <div className="mb-4 transform transition-all duration-300 hover:scale-[1.01]">
          <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-brand-100 to-brand-50 p-5 rounded-xl shadow-lg border-2 border-brand-300 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl shadow-md transform transition-transform duration-300 hover:scale-110 hover:rotate-3">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Warehouse Management</h2>
                <p className="text-gray-600 mt-1">Manage warehouses, zones, racks, and locations</p>
              </div>
            </div>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="w-full lg:w-auto justify-center px-4 py-2 min-h-[44px] lg:min-h-0 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white rounded-xl transition-all duration-300 flex items-center space-x-2 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="whitespace-nowrap">New Warehouse</span>
            </button>
          </div>
        </div>

        {/* Warehouse Space Utilization — total capacity vs. area assigned to zones */}
        {selectedWarehouse && utilization && (
          <div className="bg-white rounded-2xl p-5 shadow-lg border-2 border-gray-200 mb-4">
            <div className="flex items-start lg:items-center justify-between gap-2 mb-2">
              <div className="flex flex-col lg:flex-row lg:items-center gap-0.5 lg:gap-2 min-w-0">
                <h3 className="text-sm font-bold text-gray-900">Warehouse Space Utilization</h3>
                <span className="text-xs text-gray-500 truncate">{selectedWarehouse.name}</span>
              </div>
              <span
                className={`text-sm font-bold whitespace-nowrap flex-shrink-0 ${
                  utilization.utilizationPercent >= 100
                    ? 'text-red-600'
                    : utilization.utilizationPercent >= 80
                      ? 'text-amber-600'
                      : 'text-green-600'
                }`}
              >
                {utilization.utilizationPercent}% used
              </span>
            </div>
            <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  utilization.utilizationPercent >= 100
                    ? 'bg-red-500'
                    : utilization.utilizationPercent >= 80
                      ? 'bg-amber-500'
                      : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(utilization.utilizationPercent, 100)}%` }}
              />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
              <span className="text-gray-600">
                Total: <span className="font-semibold text-gray-900">{utilization.totalSpaceSqft.toLocaleString()} sqft</span>
              </span>
              <span className="text-gray-600">
                Assigned to zones &amp; racks: <span className="font-semibold text-brand-600">{utilization.assignedSqft.toLocaleString()} sqft</span>
              </span>
              <span className="text-gray-600">
                Available: <span className={`font-semibold ${utilization.availableSqft <= 0 ? 'text-red-600' : 'text-green-600'}`}>{utilization.availableSqft.toLocaleString()} sqft</span>
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
              <span className="text-gray-600">
                Zones (<span className="font-semibold text-gray-900">{utilization.zoneCount}</span>): <span className="font-semibold text-gray-900">{utilization.zoneSqft.toLocaleString()} sqft</span>
              </span>
              <span className="text-gray-600">
                Racks (<span className="font-semibold text-gray-900">{utilization.rackCount}</span>): <span className="font-semibold text-gray-900">{utilization.rackSqft.toLocaleString()} sqft</span>
              </span>
            </div>
          </div>
        )}

        {/* Warehouse Selector Card - relative z-20 lifts the whole card's stacking
            context (created by `transform`) above the tab bar, so the open warehouse
            dropdown paints over it instead of underneath */}
        <div className="relative z-20 bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-300 mb-4 hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.01]">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedWarehouse ? selectedWarehouse.name : 'Select Warehouse'}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {selectedWarehouse
                    ? `${selectedWarehouse.totalSpaceSqft.toLocaleString()} sqft • ${zones.length} zones • ${racks.length} racks`
                    : `${warehouses.length} warehouses available`}
                </p>
                {selectedWarehouse && (
                  <div className="mt-1 flex items-center gap-2">
                    {selectedWarehouse.code ? (
                      isEditingCode ? (
                        <div className="flex items-center gap-1">
                          <input
                            value={editingCodeValue}
                            onChange={(e) => setEditingCodeValue(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 5))}
                            maxLength={5}
                            className="w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-brand-500 uppercase"
                            placeholder="e.g. M"
                          />
                          <button
                            onClick={() => {
                              if (!editingCodeValue.trim()) { toast.error('Code cannot be empty'); return; }
                              if (!/^[A-Z0-9_-]{1,5}$/.test(editingCodeValue.trim().toUpperCase())) { toast.error('Code must be 1-5 characters: letters, numbers, hyphen or underscore'); return; }
                              updateWarehouseCodeMutation.mutate({ warehouseId: selectedWarehouse.id, code: editingCodeValue.trim().toUpperCase() });
                            }}
                            disabled={updateWarehouseCodeMutation.isPending}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >Save</button>
                          <button onClick={() => setIsEditingCode(false)} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold bg-brand-100 text-brand-800 rounded-full">
                            Code: {selectedWarehouse.code}
                          </span>
                          <button
                            onClick={() => { setIsEditingCode(true); setEditingCodeValue(selectedWarehouse.code ?? ''); }}
                            className="text-xs text-gray-400 hover:text-gray-600"
                          >Edit</button>
                        </div>
                      )
                    ) : (
                      isEditingCode ? (
                        <div className="flex items-center gap-1">
                          <input
                            value={editingCodeValue}
                            onChange={(e) => setEditingCodeValue(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 5))}
                            maxLength={5}
                            className="w-24 px-2 py-1 text-xs border border-orange-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 uppercase"
                            placeholder="e.g. M"
                          />
                          <button
                            onClick={() => {
                              if (!editingCodeValue.trim()) { toast.error('Code cannot be empty'); return; }
                              if (!/^[A-Z0-9_-]{1,5}$/.test(editingCodeValue.trim().toUpperCase())) { toast.error('Code must be 1-5 characters: letters, numbers, hyphen or underscore'); return; }
                              updateWarehouseCodeMutation.mutate({ warehouseId: selectedWarehouse.id, code: editingCodeValue.trim().toUpperCase() });
                            }}
                            disabled={updateWarehouseCodeMutation.isPending}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >Save</button>
                          <button onClick={() => setIsEditingCode(false)} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setIsEditingCode(true); setEditingCodeValue(''); }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full hover:bg-orange-200 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          No location code — set one for GRN numbering
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-3 w-full sm:w-auto">
              <Select
                options={warehouses.map((warehouse) => ({
                  value: warehouse.id,
                  label: `${warehouse.name} (${warehouse.totalSpaceSqft.toLocaleString()} sqft)`,
                }))}
                value={selectedWarehouseId ?? ''}
                onChange={(e) => setSelectedWarehouseId(e.target.value ? Number(e.target.value) : null)}
                placeholder="Select warehouse"
                className="flex-1 sm:min-w-64"
              />
            </div>
          </div>
        </div>

        {selectedWarehouse && (
          <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-300 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Company &amp; Billing Details</h4>
              {!isEditingBillingDetails && (
                <button
                  onClick={() => {
                    setBillingDetailsForm({
                      companyName: selectedWarehouse.companyName ?? '',
                      companyGstin: selectedWarehouse.companyGstin ?? '',
                      companyPan: selectedWarehouse.companyPan ?? '',
                      bankName: selectedWarehouse.bankName ?? '',
                      bankAccountNo: selectedWarehouse.bankAccountNo ?? '',
                      bankIfsc: selectedWarehouse.bankIfsc ?? '',
                      bankBranch: selectedWarehouse.bankBranch ?? '',
                      companyTagline: selectedWarehouse.companyTagline ?? '',
                      companyEmail: selectedWarehouse.companyEmail ?? '',
                      companyJurisdiction: selectedWarehouse.companyJurisdiction ?? '',
                    });
                    setBillingDetailsErrors({});
                    setIsEditingBillingDetails(true);
                  }}
                  className="text-xs text-brand-600 hover:underline"
                >Edit</button>
              )}
            </div>
            <p className="text-xs text-gray-400 mb-4">Shown on GST tax invoices billed out of this warehouse.</p>
            {isEditingBillingDetails ? (
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    label="Company Name (letterhead — defaults to warehouse name)"
                    value={billingDetailsForm.companyName}
                    onChange={(e) => setBillingDetailsForm((prev) => ({ ...prev, companyName: INPUT_SANITIZERS.legalEntityNameFormat(e.target.value) }))}
                    placeholder={selectedWarehouse.name}
                    error={billingDetailsErrors.companyName}
                  />
                  <Input
                    label="GSTIN"
                    value={billingDetailsForm.companyGstin}
                    onChange={(e) => setBillingDetailsForm((prev) => ({ ...prev, companyGstin: INPUT_SANITIZERS.gstFormat(e.target.value) }))}
                    placeholder="27ABDFK6872E1Z5"
                    error={billingDetailsErrors.companyGstin}
                  />
                  <Input
                    label="PAN"
                    value={billingDetailsForm.companyPan}
                    onChange={(e) => setBillingDetailsForm((prev) => ({ ...prev, companyPan: INPUT_SANITIZERS.panFormat(e.target.value) }))}
                    placeholder="ABDFK6872E"
                    error={billingDetailsErrors.companyPan}
                  />
                  <Input
                    label="Bank Name"
                    value={billingDetailsForm.bankName}
                    onChange={(e) => setBillingDetailsForm((prev) => ({ ...prev, bankName: INPUT_SANITIZERS.legalEntityNameFormat(e.target.value) }))}
                    placeholder="HDFC BANK LTD"
                    error={billingDetailsErrors.bankName}
                  />
                  <Input
                    label="Account No."
                    value={billingDetailsForm.bankAccountNo}
                    onChange={(e) => setBillingDetailsForm((prev) => ({ ...prev, bankAccountNo: INPUT_SANITIZERS.bankAccountFormat(e.target.value) }))}
                    placeholder="50200096136124"
                    error={billingDetailsErrors.bankAccountNo}
                  />
                  <Input
                    label="IFSC Code"
                    value={billingDetailsForm.bankIfsc}
                    onChange={(e) => setBillingDetailsForm((prev) => ({ ...prev, bankIfsc: INPUT_SANITIZERS.ifscFormat(e.target.value).slice(0, 11) }))}
                    placeholder="HDFC0001028"
                    error={billingDetailsErrors.bankIfsc}
                  />
                  <Input
                    label="Branch"
                    value={billingDetailsForm.bankBranch}
                    onChange={(e) => setBillingDetailsForm((prev) => ({ ...prev, bankBranch: INPUT_SANITIZERS.legalEntityNameFormat(e.target.value) }))}
                    placeholder="Seawoods"
                    error={billingDetailsErrors.bankBranch}
                  />
                  <Input
                    label="Tagline (shown under company name)"
                    value={billingDetailsForm.companyTagline}
                    onChange={(e) => setBillingDetailsForm((prev) => ({ ...prev, companyTagline: INPUT_SANITIZERS.legalEntityNameFormat(e.target.value) }))}
                    placeholder="(Fleet Owner & Transport Contractors.)"
                    maxLength={150}
                    error={billingDetailsErrors.companyTagline}
                  />
                  <Input
                    label="Company Email"
                    value={billingDetailsForm.companyEmail}
                    onChange={(e) => setBillingDetailsForm((prev) => ({ ...prev, companyEmail: INPUT_SANITIZERS.emailFormat(e.target.value) }))}
                    placeholder="accounts@example.in"
                    error={billingDetailsErrors.companyEmail}
                  />
                  <Input
                    label="Jurisdiction City"
                    value={billingDetailsForm.companyJurisdiction}
                    onChange={(e) => setBillingDetailsForm((prev) => ({ ...prev, companyJurisdiction: INPUT_SANITIZERS.cityNameFormat(e.target.value) }))}
                    placeholder="Mumbai"
                    error={billingDetailsErrors.companyJurisdiction}
                  />
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <button
                    onClick={handleSaveBillingDetails}
                    disabled={updateWarehouseBillingDetailsMutation.isPending}
                    className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >{updateWarehouseBillingDetailsMutation.isPending ? 'Saving…' : 'Save'}</button>
                  <button onClick={() => { setIsEditingBillingDetails(false); setBillingDetailsErrors({}); }} className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <p><span className="text-gray-400">Company Name:</span> <span className="text-gray-800">{selectedWarehouse.companyName || selectedWarehouse.name}</span></p>
                <p><span className="text-gray-400">GSTIN:</span> <span className="text-gray-800">{selectedWarehouse.companyGstin || '—'}</span></p>
                <p><span className="text-gray-400">PAN:</span> <span className="text-gray-800">{selectedWarehouse.companyPan || '—'}</span></p>
                <p><span className="text-gray-400">Bank:</span> <span className="text-gray-800">{selectedWarehouse.bankName || '—'}</span></p>
                <p><span className="text-gray-400">A/c No.:</span> <span className="text-gray-800">{selectedWarehouse.bankAccountNo || '—'}</span></p>
                <p><span className="text-gray-400">IFSC:</span> <span className="text-gray-800">{selectedWarehouse.bankIfsc || '—'}</span></p>
                <p><span className="text-gray-400">Branch:</span> <span className="text-gray-800">{selectedWarehouse.bankBranch || '—'}</span></p>
                <p><span className="text-gray-400">Tagline:</span> <span className="text-gray-800">{selectedWarehouse.companyTagline || '—'}</span></p>
                <p><span className="text-gray-400">Email:</span> <span className="text-gray-800">{selectedWarehouse.companyEmail || '—'}</span></p>
                <p><span className="text-gray-400">Jurisdiction:</span> <span className="text-gray-800">{selectedWarehouse.companyJurisdiction || '—'}</span></p>
              </div>
            )}
          </div>
        )}

            {selectedWarehouse && (
              <div>
                {/* Tab Navigation — stack on phones so Manage Zone & Rack is never clipped */}
                <div className="bg-white rounded-2xl p-2 shadow-xl border-2 border-gray-300 mb-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <button
                      onClick={() => setActiveTab('zones')}
                      className={`w-full min-h-[44px] px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                        activeTab === 'zones'
                          ? 'bg-gradient-to-r from-brand-600 to-brand-700 text-white shadow-lg shadow-blue-200'
                          : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      <span>Zones ({zones.length})</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('racks')}
                      className={`w-full min-h-[44px] px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                        activeTab === 'racks'
                          ? 'bg-gradient-to-r from-brand-600 to-brand-700 text-white shadow-lg shadow-blue-200'
                          : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      <span>Racks ({racks.length})</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('manage')}
                      className={`w-full min-h-[44px] px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                        activeTab === 'manage'
                          ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-lg shadow-emerald-200'
                          : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                      <span>Manage Zone & Rack</span>
                    </button>
                  </div>
                </div>

                {/* Zone/Rack Forms — hidden on manage tab */}
                {activeTab !== 'manage' && <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-300 mb-4 hover:shadow-2xl transition-shadow duration-300">
                  {activeTab === 'zones' && (
                    <div>
                      <div className="mb-6 pb-4 border-b border-gray-100">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">Create New Zone</h3>
                        <p className="text-sm text-gray-500">Define storage zones to organize your warehouse efficiently</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                        <Input
                          label="Zone Code"
                          value={zoneForm.code}
                          onChange={(e) => setZoneForm((prev) => ({ ...prev, code: e.target.value.toUpperCase().slice(0, 20) }))}
                          placeholder="e.g., A1, B2"
                          maxLength={20}
                          required
                        />
                        <Input
                          label="Zone Name"
                          value={zoneForm.name}
                          onChange={(e) => setZoneForm((prev) => ({ ...prev, name: e.target.value.slice(0, 20) }))}
                          placeholder="e.g., Cold Storage"
                          maxLength={20}
                          required
                        />
                        <Select
                          label="Zone Type"
                          value={zoneForm.zoneType}
                          onChange={(e) => setZoneForm((prev) => ({ ...prev, zoneType: e.target.value }))}
                          options={[
                            { value: 'regular', label: 'Regular' },
                            { value: 'heavy', label: 'Heavy (Oversized Pallets)' },
                            { value: 'lbd', label: 'LBD (Damaged Goods)' },
                          ]}
                          required
                        />
                        <Input
                          label="Pallet Size (sq ft)"
                          type="number"
                          min={String(MIN_PALLET_SIZE_SQFT)}
                          step="0.5"
                          value={zoneForm.locationSizeSqft}
                          onChange={(e) => setZoneForm((prev) => ({ ...prev, locationSizeSqft: e.target.value }))}
                          placeholder="e.g. 25, 30, 50"
                          required
                          error={
                            zoneForm.locationSizeSqft && Number(zoneForm.locationSizeSqft) < MIN_PALLET_SIZE_SQFT
                              ? `Pallet Size must be at least ${MIN_PALLET_SIZE_SQFT} sq ft`
                              : undefined
                          }
                        />
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="block text-sm font-medium text-gray-700">
                              Area Allocation
                              <span className="text-red-500 ml-1">*</span>
                            </label>
                            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs">
                              <button
                                type="button"
                                onClick={() => setZoneAreaUnit('sqft')}
                                className={`px-2 py-0.5 ${zoneAreaUnit === 'sqft' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600'}`}
                              >sqft</button>
                              <button
                                type="button"
                                onClick={() => setZoneAreaUnit('pallets')}
                                className={`px-2 py-0.5 ${zoneAreaUnit === 'pallets' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600'}`}
                              >pallets</button>
                            </div>
                          </div>
                          <input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*\.?[0-9]*"
                            value={
                              zoneForm.areaSqft === ''
                                ? ''
                                : zoneAreaUnit === 'sqft'
                                  ? zoneForm.areaSqft
                                  : String(Math.floor(Number(zoneForm.areaSqft) / (Number(zoneForm.locationSizeSqft) || SQFT_PER_PALLET)))
                            }
                            onChange={(e) => {
                              const raw = sanitizeDecimalQty(e.target.value);
                              if (raw === '') {
                                setZoneForm((prev) => ({ ...prev, areaSqft: '' }));
                                return;
                              }
                              const num = Number(raw);
                              const effectivePalletSize = Number(zoneForm.locationSizeSqft) || SQFT_PER_PALLET;
                              const sqft = zoneAreaUnit === 'sqft' ? num : Math.round(num * effectivePalletSize * 100) / 100;
                              // Hard cap: a single zone can't hold more than 500 pallets. Clamp
                              // instead of silently accepting an oversized value so the user
                              // sees exactly why the number didn't take.
                              if (sqft / effectivePalletSize > MAX_ZONE_PALLETS) {
                                toast.error(
                                  `Area Allocation cannot exceed ${MAX_ZONE_PALLETS} pallets ` +
                                    `(${(MAX_ZONE_PALLETS * effectivePalletSize).toLocaleString()} sq ft at ${effectivePalletSize} sq ft/pallet).`,
                                );
                                setZoneForm((prev) => ({ ...prev, areaSqft: String(MAX_ZONE_PALLETS * effectivePalletSize) }));
                                return;
                              }
                              setZoneForm((prev) => ({ ...prev, areaSqft: String(sqft) }));
                            }}
                            placeholder={zoneAreaUnit === 'sqft' ? 'e.g., 2000' : 'e.g., 150'}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          />
                          {zoneForm.areaSqft && Number(zoneForm.areaSqft) > 0 && (
                            <div className="mt-1 flex items-center gap-2 text-xs">
                              <span className="text-gray-500">{Number(zoneForm.areaSqft).toLocaleString()} sqft</span>
                              <span className="text-gray-400">•</span>
                              <span className="text-brand-600 font-medium">
                                {Math.floor(Number(zoneForm.areaSqft) / (Number(zoneForm.locationSizeSqft) || SQFT_PER_PALLET))} pallets
                                {!zoneForm.locationSizeSqft && ' (at default 25 sq ft/pallet — set Pallet Size for an exact count)'}
                              </span>
                            </div>
                          )}
                          {utilization && (
                            <p
                              className={`mt-1 text-xs ${
                                zoneForm.areaSqft && Number(zoneForm.areaSqft) > utilization.availableSqft + 0.001
                                  ? 'text-red-600 font-medium'
                                  : 'text-gray-400'
                              }`}
                            >
                              {utilization.availableSqft.toLocaleString()} sqft available of {utilization.totalSpaceSqft.toLocaleString()} sqft
                            </p>
                          )}
                          {zoneForm.areaSqft && zoneForm.locationSizeSqft && Number(zoneForm.locationSizeSqft) > 0 &&
                            Number(zoneForm.areaSqft) % Number(zoneForm.locationSizeSqft) !== 0 && (
                              <p className="mt-1 text-xs text-red-600 font-medium">
                                Area Allocation must be an exact multiple of Pallet Size ({zoneForm.locationSizeSqft} sq ft)
                              </p>
                          )}
                          {zoneForm.areaSqft && zoneForm.locationSizeSqft && Number(zoneForm.locationSizeSqft) > 0 &&
                            Number(zoneForm.areaSqft) / Number(zoneForm.locationSizeSqft) > MAX_ZONE_PALLETS && (
                              <p className="mt-1 text-xs text-red-600 font-medium">
                                A zone cannot exceed {MAX_ZONE_PALLETS} pallets — reduce the area or increase the pallet size.
                              </p>
                          )}
                        </div>
                        <Select
                          label="Status"
                          value={zoneForm.status}
                          onChange={(e) => setZoneForm((prev) => ({ ...prev, status: e.target.value }))}
                          options={[
                            { value: 'active', label: 'Active' },
                            { value: 'inactive', label: 'Inactive' },
                          ]}
                          required
                        />
                      </div>

                      <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
                        <button
                          onClick={() => setZoneForm({ code: '', name: '', zoneType: 'regular', status: 'active', areaSqft: '', locationSizeSqft: '' })}
                          className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                        >
                          Clear
                        </button>
                        <button
                          onClick={handleCreateZone}
                          disabled={
                            createZoneMutation.isPending ||
                            !selectedWarehouseId ||
                            !zoneForm.code.trim() ||
                            !zoneForm.name.trim() ||
                            !zoneForm.zoneType.trim() ||
                            !zoneForm.areaSqft ||
                            Number(zoneForm.areaSqft) <= 0 ||
                            !zoneForm.status.trim() ||
                            !zoneForm.locationSizeSqft ||
                            Number(zoneForm.locationSizeSqft) < MIN_PALLET_SIZE_SQFT ||
                            Number(zoneForm.areaSqft) % Number(zoneForm.locationSizeSqft) !== 0
                          }
                          className="px-6 py-2.5 bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-md hover:shadow-lg"
                        >
                          {createZoneMutation.isPending ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Creating...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              Create Zone
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {activeTab === 'racks' && (
                    <div>
                      <div className="mb-6 pb-4 border-b border-gray-100">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">Create New Rack</h3>
                        <p className="text-sm text-gray-500">Add storage racks to organize inventory within zones</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                        <Input
                          label="Rack Code"
                          value={rackForm.code}
                          onChange={(e) => setRackForm((prev) => ({ ...prev, code: e.target.value.toUpperCase().slice(0, 20) }))}
                          placeholder="e.g., R001, RACK-A1"
                          maxLength={20}
                          required
                        />
                        <Input
                          label="Rack Name"
                          value={rackForm.name}
                          onChange={(e) => setRackForm((prev) => ({ ...prev, name: e.target.value.slice(0, 20) }))}
                          placeholder="e.g., Heavy Equipment Rack"
                          maxLength={20}
                          required
                        />
                        <Select
                          label="Rack Type"
                          value={rackForm.rackType}
                          onChange={(e) => setRackForm((prev) => ({ ...prev, rackType: e.target.value }))}
                          options={[
                            { value: 'regular', label: 'Regular' },
                            { value: 'heavy', label: 'Heavy (Oversized Pallets)' },
                            { value: 'lbd', label: 'LBD (Damaged Goods)' },
                          ]}
                          required
                        />
                        <Input
                          label="Pallet Size (sq ft)"
                          type="number"
                          min={String(MIN_PALLET_SIZE_SQFT)}
                          step="0.5"
                          value={rackForm.locationSizeSqft}
                          onChange={(e) => setRackForm((prev) => ({ ...prev, locationSizeSqft: e.target.value }))}
                          placeholder="e.g. 25, 30, 50"
                          required
                          error={
                            rackForm.locationSizeSqft && Number(rackForm.locationSizeSqft) < MIN_PALLET_SIZE_SQFT
                              ? `Pallet Size must be at least ${MIN_PALLET_SIZE_SQFT} sq ft`
                              : undefined
                          }
                        />
                        <div>
                          <Input
                            label="Rack Count"
                            type="number"
                            min="1"
                            max={String(MAX_RACK_PALLETS)}
                            step="1"
                            value={rackForm.rackCount}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/[^0-9]/g, '');
                              if (digits !== '' && Number(digits) > MAX_RACK_PALLETS) {
                                toast.error(`Rack Count cannot exceed ${MAX_RACK_PALLETS} pallet slots.`);
                                setRackForm((prev) => ({ ...prev, rackCount: String(MAX_RACK_PALLETS) }));
                                return;
                              }
                              setRackForm((prev) => ({ ...prev, rackCount: digits }));
                            }}
                            placeholder="e.g. 2"
                            required
                          />
                          {rackForm.rackCount && rackForm.locationSizeSqft && Number(rackForm.locationSizeSqft) > 0 && (
                            <p className="mt-1 text-xs text-gray-500">
                              {Number(rackForm.rackCount)} × {rackForm.locationSizeSqft} sq ft = {' '}
                              <span className="font-medium text-brand-600">
                                {(Number(rackForm.rackCount) * Number(rackForm.locationSizeSqft)).toLocaleString()} sqft
                              </span>
                            </p>
                          )}
                          {utilization && (
                            <p className="mt-1 text-xs text-gray-400">
                              {utilization.availableSqft.toLocaleString()} sqft available of {utilization.totalSpaceSqft.toLocaleString()} sqft
                            </p>
                          )}
                        </div>
                        <Select
                          label="Status"
                          value={rackForm.status}
                          onChange={(e) => setRackForm((prev) => ({ ...prev, status: e.target.value }))}
                          options={[
                            { value: 'active', label: 'Active' },
                            { value: 'inactive', label: 'Inactive' },
                          ]}
                          required
                        />
                      </div>

                      <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
                        <button
                          onClick={() => setRackForm({ code: '', name: '', rackType: 'regular', status: 'active', locationSizeSqft: '', rackCount: '' })}
                          className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                        >
                          Clear
                        </button>
                        <button
                          onClick={handleCreateRack}
                          disabled={
                            createRackMutation.isPending ||
                            !selectedWarehouseId ||
                            !rackForm.code.trim() ||
                            !rackForm.name.trim() ||
                            !rackForm.rackType.trim() ||
                            !rackForm.status.trim() ||
                            !rackForm.locationSizeSqft ||
                            Number(rackForm.locationSizeSqft) < MIN_PALLET_SIZE_SQFT ||
                            !rackForm.rackCount ||
                            !Number.isInteger(Number(rackForm.rackCount)) ||
                            Number(rackForm.rackCount) <= 0
                          }
                          className="px-6 py-2.5 bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-md hover:shadow-lg"
                        >
                          {createRackMutation.isPending ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Creating...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              Create Rack
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>}

                {/* Data Tables */}
                <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:border-primary-300">
                  {activeTab === 'zones' ? (
                    <div>
                      <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300">
                        <div className="flex items-center">
                          <svg className="w-6 h-6 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                          </svg>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Zone List
                            {zones.length > 0 && (
                              <span className="text-sm font-normal text-gray-500 ml-2">
                                ({zones.length} {zones.length === 1 ? 'zone' : 'zones'})
                              </span>
                            )}
                          </h3>
                        </div>
                      </div>
                      {zonesError ? (
                        <div className="p-8 text-center">
                          <div className="text-red-500 text-4xl mb-4">⚠️</div>
                          <p className="text-red-600 font-medium">Failed to load zones</p>
                          <p className="text-sm text-gray-500 mt-2">{getErrorMessage(zonesErrorData, 'Unknown error')}</p>
                        </div>
                      ) : (
                        <div className="px-1 py-3 lg:p-5">{zoneTable}</div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300">
                        <div className="flex items-center">
                          <svg className="w-6 h-6 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Rack List
                            {racks.length > 0 && (
                              <span className="text-sm font-normal text-gray-500 ml-2">
                                ({racks.length} {racks.length === 1 ? 'rack' : 'racks'})
                              </span>
                            )}
                          </h3>
                        </div>
                      </div>
                      {racksError ? (
                        <div className="p-8 text-center">
                          <div className="text-red-500 text-4xl mb-4">⚠️</div>
                          <p className="text-red-600 font-medium">Failed to load racks</p>
                          <p className="text-sm text-gray-500 mt-2">{getErrorMessage(racksErrorData, 'Unknown error')}</p>
                        </div>
                      ) : (
                        <div className="px-1 py-3 lg:p-5">{rackTable}</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Location Management - Only show for Zones tab */}
                {activeTab === 'zones' && (
                  <div ref={zoneLocationSectionRef} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mt-6">

                    <div className="mb-6 pb-4 border-b border-gray-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Location Management</h3>
                      <p className="text-sm text-gray-500">Locations are generated automatically when a zone is created (Area Allocation ÷ Pallet Size)</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <Select
                        label="Select Zone"
                        options={zones.map((zone) => ({
                          value: zone.id,
                          label: `${zone.code}${zone.name ? ` - ${zone.name}` : ''}`,
                        }))}
                        value={selectedZoneId ?? ''}
                        onChange={(e) => setSelectedZoneId(e.target.value ? Number(e.target.value) : null)}
                        placeholder="Choose zone"
                      />
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-600 mb-4 px-1">
                      <span>Current locations: <span className="font-semibold text-gray-900">{totalZoneLocationsCount}</span></span>
                    </div>

                    {selectedZoneId && (
                      <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                        <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 flex items-center justify-between">
                          <h5 className="text-sm font-bold text-gray-900">Zone Locations ({zoneOccupancyLocations.length || totalZoneLocationsCount})</h5>
                          {zoneOccupancyLocations.length > 0 && (
                            <span className="text-xs font-medium text-gray-500">
                              <span className="text-amber-700">{zoneOccupancyLocations.filter((l) => l.status === 'occupied').length} occupied</span>
                              {' · '}
                              <span className="text-green-700">{zoneOccupancyLocations.filter((l) => l.status !== 'occupied').length} available</span>
                            </span>
                          )}
                        </div>
                        <div className="px-4 py-2.5 bg-white border-b border-gray-200">
                          <div className="relative max-w-sm">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                              type="text"
                              value={zoneLocationSearch}
                              onChange={(e) => setZoneLocationSearch(e.target.value)}
                              placeholder="Search location, status, client, product, batch…"
                              className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                            {zoneLocationSearch && (
                              <button
                                type="button"
                                onClick={() => setZoneLocationSearch('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                title="Clear search"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          {zoneLocationSearch && (
                            <p className="text-xs text-gray-500 mt-1.5">
                              {filteredZoneOccupancyLocations.length} of {zoneOccupancyLocations.length} locations match
                            </p>
                          )}
                        </div>
                        {zoneOccupancyError ? (
                          <div className="p-4 text-center">
                            <p className="text-red-600 text-sm">{getErrorMessage(zoneOccupancyErrorData, 'Failed to load locations')}</p>
                          </div>
                        ) : (
                          <ResponsiveTable
                            key={`zone-occupancy-${selectedZoneId}`}
                            columns={[
                              {
                                key: 'locationCode',
                                label: 'Location',
                                width: '140px',
                                render: (value) => <span className="font-mono font-semibold text-gray-900">{value}</span>,
                              },
                              {
                                key: 'status',
                                label: 'Status',
                                width: '130px',
                                render: (value) => (
                                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full ${
                                    value === 'occupied' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                                  }`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${value === 'occupied' ? 'bg-amber-500' : 'bg-green-500'}`} />
                                    {value === 'occupied' ? 'Occupied' : 'Available'}
                                  </span>
                                ),
                              },
                              {
                                key: 'occupiedPercentage',
                                label: 'Occupied %',
                                width: '120px',
                                render: (value) => {
                                  const pct = Number(value ?? 0);
                                  return (
                                    <div className="flex items-center gap-1.5">
                                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden" style={{ minWidth: 60 }}>
                                        <div
                                          className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-red-400' : pct > 0 ? 'bg-amber-400' : 'bg-green-400'}`}
                                          style={{ width: `${Math.min(pct, 100)}%` }}
                                        />
                                      </div>
                                      <span className={`text-xs font-semibold ${pct >= 100 ? 'text-red-600' : 'text-gray-700'}`}>{pct}%</span>
                                    </div>
                                  );
                                },
                              },
                              {
                                key: 'availableSqFt',
                                label: 'Sq ft (avail/cap)',
                                width: '140px',
                                render: (value, row) => {
                                  const capacity = (row as any).locationSizeSqFt as number | null;
                                  if (capacity === null) {
                                    return <span className="text-gray-300 text-xs">—</span>;
                                  }
                                  const avail = Number(value ?? 0);
                                  return (
                                    <span className={`text-xs font-semibold ${avail === 0 ? 'text-red-500' : 'text-green-700'}`}>
                                      {avail} / {capacity} sq ft
                                    </span>
                                  );
                                },
                              },
                              {
                                key: 'clientName',
                                label: 'Client',
                                render: (value) => value || <span className="text-gray-300">—</span>,
                              },
                              {
                                key: 'productDescription',
                                label: 'Product',
                                render: (value) => value || <span className="text-gray-300">—</span>,
                              },
                              {
                                key: 'batchNo',
                                label: 'Batch',
                                width: '140px',
                                render: (value) => value ? <span className="font-mono text-xs text-gray-500">{value}</span> : <span className="text-gray-300">—</span>,
                              },
                            ]}
                            data={filteredZoneOccupancyLocations}
                            isLoading={zoneOccupancyLoading}
                            hasMore={false}
                            height="500px"
                            emptyMessage={zoneLocationSearch ? 'No locations match your search.' : 'No locations found for this zone.'}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Location Management - Only show for Racks tab */}
                {activeTab === 'racks' && (
                  <div ref={locationSectionRef} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mt-6">
                  
                    <div className="mb-6 pb-4 border-b border-gray-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Location Management</h3>
                      <p className="text-sm text-gray-500">Locations are generated automatically when a rack is created (Rack Count × Pallet Size) — or top up an existing rack below</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <Select
                        label="Select Rack"
                        options={racks.map((rack) => ({
                          value: rack.id,
                          label: `${rack.code}${rack.name ? ` - ${rack.name}` : ''}`,
                        }))}
                        value={selectedRackId ?? ''}
                        onChange={(e) => setSelectedRackId(e.target.value ? Number(e.target.value) : null)}
                        placeholder="Choose rack"
                      />
                      <Input
                        label="Target Location Count"
                        type="number"
                        value={locationsForm.count}
                        onChange={(e) => setLocationsForm({ count: e.target.value })}
                        placeholder="Enter count"
                      />
                      <div className="flex items-end">
                        <button
                          onClick={handleGenerateLocations}
                          disabled={generateLocationsMutation.isPending || !selectedRackId}
                          className="w-full px-4 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-md hover:shadow-lg"
                        >
                          {generateLocationsMutation.isPending ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Generating...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              Generate
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-600 mb-4 px-1">
                      <span>Current locations: <span className="font-semibold text-gray-900">{totalLocationsCount}</span></span>
                      <span className="text-gray-400">Enter a higher number to add more</span>
                    </div>
                    
                    {selectedRackId && (
                      <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                        <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 flex items-center justify-between">
                          <h5 className="text-sm font-bold text-gray-900">Rack Locations ({rackOccupancyLocations.length || totalLocationsCount})</h5>
                          {rackOccupancyLocations.length > 0 && (
                            <span className="text-xs font-medium text-gray-500">
                              <span className="text-amber-700">{rackOccupancyLocations.filter((l) => l.status === 'occupied').length} occupied</span>
                              {' · '}
                              <span className="text-green-700">{rackOccupancyLocations.filter((l) => l.status !== 'occupied').length} available</span>
                            </span>
                          )}
                        </div>
                        <div className="px-4 py-2.5 bg-white border-b border-gray-200">
                          <div className="relative max-w-sm">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                              type="text"
                              value={rackLocationSearch}
                              onChange={(e) => setRackLocationSearch(e.target.value)}
                              placeholder="Search location, status, client, product, batch…"
                              className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                            {rackLocationSearch && (
                              <button
                                type="button"
                                onClick={() => setRackLocationSearch('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                title="Clear search"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          {rackLocationSearch && (
                            <p className="text-xs text-gray-500 mt-1.5">
                              {filteredRackOccupancyLocations.length} of {rackOccupancyLocations.length} locations match
                            </p>
                          )}
                        </div>
                        {rackOccupancyError ? (
                          <div className="p-4 text-center">
                            <p className="text-red-600 text-sm">{getErrorMessage(rackOccupancyErrorData, 'Failed to load locations')}</p>
                          </div>
                        ) : (
                          <ResponsiveTable
                            key={`rack-occupancy-${selectedRackId}`}
                            columns={[
                              {
                                key: 'locationCode',
                                label: 'Location',
                                width: '140px',
                                render: (value) => <span className="font-mono font-semibold text-gray-900">{value}</span>,
                              },
                              {
                                key: 'status',
                                label: 'Status',
                                width: '130px',
                                render: (value) => (
                                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full ${
                                    value === 'occupied' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                                  }`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${value === 'occupied' ? 'bg-amber-500' : 'bg-green-500'}`} />
                                    {value === 'occupied' ? 'Occupied' : 'Available'}
                                  </span>
                                ),
                              },
                              {
                                key: 'occupiedPercentage',
                                label: 'Occupied %',
                                width: '120px',
                                render: (value, row) => {
                                  const pct = Number(value ?? 0);
                                  return (
                                    <div className="flex flex-col gap-1">
                                      <div className="flex items-center gap-1.5">
                                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden" style={{ minWidth: 60 }}>
                                          <div
                                            className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-red-400' : pct > 0 ? 'bg-amber-400' : 'bg-green-400'}`}
                                            style={{ width: `${Math.min(pct, 100)}%` }}
                                          />
                                        </div>
                                        <span className={`text-xs font-semibold ${pct >= 100 ? 'text-red-600' : 'text-gray-700'}`}>{pct}%</span>
                                      </div>
                                    </div>
                                  );
                                },
                              },
                              {
                                key: 'availableSqFt',
                                label: 'Sq ft (avail/cap)',
                                width: '140px',
                                render: (value, row) => {
                                  const capacity = (row as any).locationSizeSqFt as number | null;
                                  if (capacity === null) {
                                    return <span className="text-gray-300 text-xs">—</span>;
                                  }
                                  const avail = Number(value ?? 0);
                                  return (
                                    <span className={`text-xs font-semibold ${avail === 0 ? 'text-red-500' : 'text-green-700'}`}>
                                      {avail} / {capacity} sq ft
                                    </span>
                                  );
                                },
                              },
                              {
                                key: 'clientName',
                                label: 'Client',
                                render: (value) => value || <span className="text-gray-300">—</span>,
                              },
                              {
                                key: 'productDescription',
                                label: 'Product',
                                render: (value) => value || <span className="text-gray-300">—</span>,
                              },
                              {
                                key: 'batchNo',
                                label: 'Batch',
                                width: '140px',
                                render: (value) => value ? <span className="font-mono text-xs text-gray-500">{value}</span> : <span className="text-gray-300">—</span>,
                              },
                            ]}
                            data={filteredRackOccupancyLocations}
                            isLoading={rackOccupancyLoading}
                            hasMore={false}
                            height="500px"
                            emptyMessage={rackLocationSearch ? 'No locations match your search.' : 'No locations found for this rack.'}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Manage Zone & Rack Tab ── */}
                {activeTab === 'manage' && (
                  <div className="mt-4">
                    {manageLoading ? (
                      <div className="bg-white rounded-2xl p-12 shadow-xl border-2 border-gray-300 text-center">
                        <svg className="animate-spin h-10 w-10 text-emerald-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <p className="text-gray-500 font-medium">Loading warehouse data…</p>
                      </div>
                    ) : selectedManageZoneId != null ? (
                      /* ── ZONE DRILL-DOWN ── */
                      (() => {
                        const totalStored = selectedZoneProducts.reduce((s, p) => s + p.palletCount, 0);
                        return (
                          <div>
                            <div className="bg-white rounded-2xl p-5 shadow-xl border-2 border-gray-300 mb-4 flex items-center gap-4">
                              <button onClick={() => { setSelectedManageZoneId(null); clearMergeSelection(); }} className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors flex-shrink-0">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                All Zones
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="text-lg font-bold text-gray-900">{selectedZone?.code}</h3>
                                  {selectedZone?.name && <span className="text-gray-500">— {selectedZone.name}</span>}
                                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">{selectedZone?.zoneType}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${selectedZone?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>{selectedZone?.status}</span>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">
                                  {selectedZone?.capacityPallets != null
                                    ? <>{totalStored} / {selectedZone.capacityPallets} pallets stored{selectedZone.utilizationPct != null && <span className="ml-1 font-semibold text-gray-700">· {selectedZone.utilizationPct}% utilized</span>}</>
                                    : <>{totalStored} pallets stored in this zone</>}
                                </p>
                              </div>
                            </div>
                            {mergeToolbar}
                            <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden">
                              {selectedZoneProducts.length === 0 ? (
                                <div className="p-10 text-center text-gray-400">No pallets stored in this zone.</div>
                              ) : (
                                <div className="overflow-auto max-h-[360px]">
                                  <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                                      <tr>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-12">Merge</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Batch</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date Stored</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Pallets</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Size (sqft)</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {selectedZoneProducts.map((p, i) => (
                                        <tr key={p.id} className={`border-t border-gray-50 transition-colors ${isSelectedForMerge(p.id) ? 'bg-emerald-50' : 'hover:bg-amber-50'}`}>
                                          <td className="px-4 py-3 text-center">
                                            <input
                                              type="checkbox"
                                              checked={isSelectedForMerge(p.id)}
                                              disabled={!canSelectForMerge(p)}
                                              onChange={() => toggleMergeSelect(p)}
                                              title={canSelectForMerge(p) ? 'Select to merge' : 'Only same-client pallets can be merged together'}
                                              className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                                            />
                                          </td>
                                          <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                                          <td className="px-4 py-3 font-medium text-gray-900">{p.clientName}</td>
                                          <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{p.productDescription}</td>
                                          <td className="px-4 py-3 text-gray-500 font-mono text-xs">{p.batchNo || '—'}</td>
                                          <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                                            {p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                          </td>
                                          <td className="px-4 py-3 text-center font-bold text-brand-700">{p.palletCount}</td>
                                          <td className="px-4 py-3 text-center">
                                            {p.palletSizeSqft ? (
                                              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold border border-amber-200">
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                                </svg>
                                                {p.palletSizeSqft} sqft
                                              </span>
                                            ) : (
                                              <span className="text-gray-400 text-xs">Standard</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()
                    ) : selectedManageRackId != null ? (
                      /* ── RACK DRILL-DOWN ── */
                      (() => {
                        const rackLocs = selectedRackManage?.locations ?? [];
                        const occupiedCount = rackLocs.filter((l) => l.status !== 'available' || locationToStorageMap.has(l.id)).length;
                        return (
                          <div>
                            <div className="bg-white rounded-2xl p-5 shadow-xl border-2 border-gray-300 mb-4 flex items-center gap-4">
                              <button onClick={() => { setSelectedManageRackId(null); clearMergeSelection(); }} className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors flex-shrink-0">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                All Racks
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="text-lg font-bold text-gray-900">{selectedRackManage?.code}</h3>
                                  {selectedRackManage?.name && <span className="text-gray-500">— {selectedRackManage.name}</span>}
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${selectedRackManage?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>{selectedRackManage?.status}</span>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">{occupiedCount} of {rackLocs.length} locations occupied · {rackLocs.length - occupiedCount} available</p>
                              </div>
                            </div>
                            {mergeToolbar}
                            <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden">
                              {rackLocs.length === 0 ? (
                                <div className="p-10 text-center text-gray-400">No locations found for this rack.</div>
                              ) : (
                                <div className="overflow-auto max-h-[360px]">
                                  <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                                      <tr>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-12">Merge</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Location</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Batch</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Qty</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Pallets</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {rackLocs.map((loc) => {
                                        const storage = locationToStorageMap.get(loc.id);
                                        const isOccupied = loc.status !== 'available' || !!storage;
                                        const selected = !!storage && isSelectedForMerge(storage.id);
                                        return (
                                          <tr key={loc.id} className={`border-t border-gray-50 transition-colors ${selected ? 'bg-emerald-50' : isOccupied ? 'hover:bg-amber-50' : 'hover:bg-green-50'}`}>
                                            <td className="px-4 py-3 text-center">
                                              {storage ? (
                                                <input
                                                  type="checkbox"
                                                  checked={selected}
                                                  disabled={!canSelectForMerge(storage)}
                                                  onChange={() => toggleMergeSelect(storage)}
                                                  title={canSelectForMerge(storage) ? 'Select to merge' : 'Only same-client pallets can be merged together'}
                                                  className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                                                />
                                              ) : (
                                                <span className="text-gray-300">—</span>
                                              )}
                                            </td>
                                            <td className="px-4 py-3 font-mono font-bold text-gray-900">{loc.code}</td>
                                            <td className="px-4 py-3">
                                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${isOccupied ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${isOccupied ? 'bg-amber-500' : 'bg-green-500'}`} />
                                                {isOccupied ? 'Occupied' : 'Available'}
                                              </span>
                                            </td>
                                            <td className="px-4 py-3 text-gray-700">{storage?.clientName ?? <span className="text-gray-300">—</span>}</td>
                                            <td className="px-4 py-3 text-gray-600 max-w-[180px] truncate">{storage?.productDescription ?? <span className="text-gray-300">—</span>}</td>
                                            <td className="px-4 py-3 text-gray-500 font-mono text-xs">{storage?.batchNo || <span className="text-gray-300">—</span>}</td>
                                            <td className="px-4 py-3 text-center font-semibold text-gray-800">{storage ? storage.goodQty : <span className="text-gray-300">—</span>}</td>
                                            <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                                              {storage?.createdAt ? new Date(storage.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-4 py-3 text-center font-bold text-brand-700">{storage ? storage.palletCount : <span className="text-gray-300">—</span>}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      /* ── TOP-LEVEL LIST ── */
                      <>
                        {/* Merge / Share Pallets action bar */}
                        <div className="bg-gradient-to-r from-emerald-50 to-white rounded-2xl p-4 shadow-xl border-2 border-emerald-200 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                            </div>
                            <div>
                              <h3 className="text-base font-bold text-gray-900">Merge / Share Pallets</h3>
                              <p className="text-sm text-gray-500">Combine two or more pallets of the same client into one to free up space.</p>
                            </div>
                          </div>
                          <button
                            onClick={() => { setSelectedForMerge([]); setMergeTargetId(null); setShowMergePicker(true); }}
                            disabled={pallets.length < 2}
                            title={pallets.length < 2 ? 'Need at least two stored pallets to merge' : 'Pick pallets to merge'}
                            className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white text-sm font-semibold rounded-lg transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 flex-shrink-0"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                            Merge Pallets
                          </button>
                        </div>

                        {/* Summary cards */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                          {[
                            { label: 'Total Zones', value: managedZones.length, sub: `${managedZones.filter(z => z.totalPalletsStored > 0).length} in use`, color: 'bg-amber-100', textColor: 'text-amber-600' },
                            { label: 'Available Zones', value: managedZones.filter(z => z.totalPalletsStored === 0).length, sub: 'empty zones', color: 'bg-green-100', textColor: 'text-green-600' },
                            { label: 'Total Racks', value: managedRacks.length, sub: `${totalRackLocations} locations`, color: 'bg-brand-100', textColor: 'text-brand-600' },
                            { label: 'Free Locations', value: totalRackLocations - occupiedRackLocations, sub: `${occupiedRackLocations} occupied`, color: 'bg-emerald-100', textColor: 'text-emerald-600' },
                          ].map(s => (
                            <div key={s.label} className="bg-white rounded-xl p-4 shadow-md border border-gray-200 flex items-center gap-3">
                              <div className={`w-10 h-10 ${s.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                <span className={`text-lg font-bold ${s.textColor}`}>{s.value}</span>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 font-medium">{s.label}</p>
                                <p className="text-xs text-gray-400">{s.sub}</p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Manage Pallets table — move & merge */}
                        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 mb-4 overflow-hidden">
                          <div className="p-4 bg-gradient-to-r from-emerald-50 to-white border-b-2 border-gray-200 flex items-center justify-between">
                            <h3 className="text-base font-bold text-gray-900">
                              Manage Pallets ({filteredPallets.length}{palletFiltersActive ? ` of ${pallets.length}` : ''})
                            </h3>
                            <span className="text-xs text-gray-500">Move a pallet to a new location, or tick two of the same client to merge.</span>
                          </div>
                          {pallets.length > 0 && (
                            <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                <div className="relative lg:col-span-1">
                                  <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                  <input
                                    type="text"
                                    value={palletSearch}
                                    onChange={(e) => setPalletSearch(e.target.value)}
                                    placeholder="Search product, client, batch, location…"
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                                  />
                                </div>
                                <Select
                                  options={palletClientOptions}
                                  value={palletClientFilter}
                                  onChange={(e) => setPalletClientFilter(e.target.value)}
                                  placeholder="All Clients"
                                />
                                <Select
                                  options={palletProductOptions}
                                  value={palletProductFilter}
                                  onChange={(e) => setPalletProductFilter(e.target.value)}
                                  placeholder="All Products"
                                />
                                <Select
                                  options={palletLocationOptions}
                                  value={palletLocationFilter}
                                  onChange={(e) => setPalletLocationFilter(e.target.value)}
                                  placeholder="All Locations"
                                />
                              </div>
                              {palletFiltersActive && (
                                <div className="mt-2 flex justify-end">
                                  <button onClick={clearPalletFilters} className="text-xs font-medium text-gray-500 hover:text-gray-700 flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    Clear filters
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="p-4">{mergeToolbar}</div>
                          {pallets.length === 0 ? (
                            <div className="p-10 text-center text-gray-400">No stored pallets in this warehouse yet.</div>
                          ) : filteredPallets.length === 0 ? (
                            <div className="p-10 text-center text-gray-400">
                              No pallets match your filters.
                              <button onClick={clearPalletFilters} className="ml-2 text-emerald-600 font-medium hover:underline">Clear filters</button>
                            </div>
                          ) : (
                            <div className="overflow-auto max-h-[360px]">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                                  <tr>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-12">Merge</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Batch</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Current Location</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Pallets</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Size (sqft)</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredPallets.map((p) => {
                                    const checked = isSelectedForMerge(p.id);
                                    const place = p.storageType === 'rack'
                                      ? `${p.rackCode ?? 'Rack'} · ${p.locationCode ?? '—'}`
                                      : `${p.zoneLocationCode ?? p.zoneCode ?? 'Zone'}`;
                                    return (
                                      <tr key={p.id} className={`border-t border-gray-50 transition-colors ${checked ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}>
                                        <td className="px-4 py-3 text-center">
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={!canSelectForMerge(p)}
                                            onChange={() => toggleMergeSelect(p)}
                                            title={canSelectForMerge(p) ? 'Select to merge' : 'Only same-client pallets can be merged together'}
                                            className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                                          />
                                        </td>
                                        <td className="px-4 py-3 font-medium text-gray-900">{p.clientName}</td>
                                        <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{p.productDescription}</td>
                                        <td className="px-4 py-3 text-gray-500 font-mono text-xs">{p.batchNo || '—'}</td>
                                        <td className="px-4 py-3">
                                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${p.storageType === 'rack' ? 'bg-brand-100 text-brand-700' : 'bg-amber-100 text-amber-700'}`}>
                                            {p.storageType === 'rack' ? 'Rack' : 'Ground'} · {place}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 text-center font-bold text-brand-700">{p.palletCount}</td>
                                        <td className="px-4 py-3 text-center">
                                          {p.palletSizeSqft ? (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold border border-amber-200">
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                              </svg>
                                              {p.palletSizeSqft}
                                            </span>
                                          ) : (
                                            <span className="text-gray-400 text-xs">Standard</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          <button
                                            onClick={() => openRelocate(p)}
                                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-50 hover:bg-brand-100 text-brand-700 rounded-lg text-xs font-semibold transition-colors border border-brand-200"
                                          >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                                            Move
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        {/* Zones table */}
                        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 mb-4 overflow-hidden">
                          <div className="p-4 bg-gradient-to-r from-amber-50 to-white border-b-2 border-gray-200 flex flex-wrap items-center justify-between gap-2">
                            <h3 className="text-base font-bold text-gray-900">Zones ({managedZones.length})</h3>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium">
                              {zonesUtilization.pct != null && (
                                <span className="text-gray-700">{zonesUtilization.stored} / {zonesUtilization.capacity} pallets · {zonesUtilization.pct}% utilized</span>
                              )}
                              <span className="text-amber-700">{managedZones.filter(z => z.totalPalletsStored > 0).length} in use</span>
                              <span className="text-green-700">{managedZones.filter(z => z.totalPalletsStored === 0).length} available</span>
                            </div>
                          </div>
                          {managedZones.length === 0 ? (
                            <div className="p-10 text-center text-gray-400">No zones found. Create zones in the Zones tab.</div>
                          ) : (
                            <>
                            <div className="lg:hidden px-2 py-3">
                              <MobileCardList columns={managedZoneCardColumns} data={managedZones} />
                            </div>
                            <div className="hidden lg:block overflow-auto max-h-[360px]">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Zone</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Availability</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Pallets Stored</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase min-w-[140px]">Utilization</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">View</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {managedZones.map(zone => {
                                    const isOccupied = zone.totalPalletsStored > 0;
                                    const utilPct = zone.utilizationPct;
                                    return (
                                      <tr key={zone.id} className={`border-t border-gray-50 transition-colors ${isOccupied ? 'hover:bg-amber-50' : 'hover:bg-green-50'}`}>
                                        <td className="px-4 py-3">
                                          <div className="font-bold text-gray-900">{zone.code}</div>
                                          {zone.name && <div className="text-xs text-gray-500">{zone.name}</div>}
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">{zone.zoneType}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${isOccupied ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${isOccupied ? 'bg-amber-500' : 'bg-green-500'}`} />
                                            {isOccupied ? 'In Use' : 'Available'}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                          {zone.capacityPallets != null
                                            ? <span className={isOccupied ? 'font-semibold text-amber-700' : 'text-green-600 font-medium'}>{zone.totalPalletsStored} / {zone.capacityPallets} pallets</span>
                                            : isOccupied
                                              ? <span className="font-semibold text-amber-700">{zone.totalPalletsStored} pallets</span>
                                              : <span className="text-green-600 font-medium">0 — Free</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                          {utilPct != null ? (
                                            <div className="flex items-center gap-2">
                                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden min-w-[80px]">
                                                <div className={`h-full rounded-full ${utilPct > 85 ? 'bg-red-500' : utilPct > 60 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${utilPct}%` }} />
                                              </div>
                                              <span className="text-xs text-gray-600 w-10 text-right font-medium">{utilPct}%</span>
                                            </div>
                                          ) : (
                                            <span className="text-xs text-gray-400 italic">No capacity set</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          <button onClick={() => { clearMergeSelection(); setSelectedManageZoneId(zone.id); }} className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-xs font-semibold transition-colors border border-amber-200">
                                            View <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            </>
                          )}
                        </div>

                        {/* Racks table */}
                        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden">
                          <div className="p-4 bg-gradient-to-r from-brand-50 to-white border-b-2 border-gray-200 flex flex-wrap items-center justify-between gap-2">
                            <h3 className="text-base font-bold text-gray-900">Racks ({managedRacks.length})</h3>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium">
                              <span className="text-gray-700">{racksUtilizationPct}% utilized</span>
                              <span className="text-amber-700">{occupiedRackLocations} locations occupied</span>
                              <span className="text-green-700">{totalRackLocations - occupiedRackLocations} free</span>
                            </div>
                          </div>
                          {managedRacks.length === 0 ? (
                            <div className="p-10 text-center text-gray-400">No racks found. Create racks in the Racks tab.</div>
                          ) : (
                            <>
                            <div className="lg:hidden px-2 py-3">
                              <MobileCardList columns={managedRackCardColumns} data={managedRacks} />
                            </div>
                            <div className="hidden lg:block overflow-auto max-h-[360px]">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Rack</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Locations</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase min-w-[140px]">Utilization</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">View</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {managedRacks.map(rack => {
                                    const totalLocs = rack.locations.length;
                                    const occupiedLocs = rack.locations.filter(l => l.status !== 'available' || locationToStorageMap.has(l.id)).length;
                                    const availLocs = totalLocs - occupiedLocs;
                                    const utilPct = totalLocs > 0 ? Math.round((occupiedLocs / totalLocs) * 100) : 0;
                                    return (
                                      <tr key={rack.id} className={`border-t border-gray-50 transition-colors ${occupiedLocs > 0 ? 'hover:bg-brand-50' : 'hover:bg-green-50'}`}>
                                        <td className="px-4 py-3">
                                          <div className="font-bold text-gray-900">{rack.code}</div>
                                          {rack.name && <div className="text-xs text-gray-500">{rack.name}</div>}
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${rack.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>{rack.status}</span>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                          <span className="font-semibold text-amber-700">{occupiedLocs}</span> occupied · <span className="font-semibold text-green-700">{availLocs}</span> free · {totalLocs} total
                                        </td>
                                        <td className="px-4 py-3">
                                          <div className="flex items-center gap-2">
                                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden min-w-[80px]">
                                              <div className={`h-full rounded-full ${utilPct > 85 ? 'bg-red-500' : utilPct > 60 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${utilPct}%` }} />
                                            </div>
                                            <span className="text-xs text-gray-600 w-10 text-right font-medium">{utilPct}%</span>
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          <button onClick={() => { clearMergeSelection(); setSelectedManageRackId(rack.id); }} className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-50 hover:bg-brand-100 text-brand-700 rounded-lg text-xs font-semibold transition-colors border border-brand-200">
                                            View <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {!selectedWarehouse && warehouses.length === 0 && (
              <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-brand-100 to-brand-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <svg className="w-10 h-10 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Warehouses Found</h3>
                <p className="text-gray-500 mb-8 max-w-md mx-auto">Get started by creating your first warehouse to manage zones, racks, and locations</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-8 py-3 bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white rounded-lg transition-all font-medium inline-flex items-center shadow-lg hover:shadow-xl"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Your First Warehouse
                </button>
              </div>
            )}

            {!selectedWarehouse && warehouses.length > 0 && (
              <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Select a Warehouse</h3>
                <p className="text-gray-500 max-w-md mx-auto">Choose a warehouse from the dropdown above to manage its structure</p>
              </div>
            )}
      </div>

      {/* Create Warehouse Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end lg:items-center justify-center z-50 p-3 lg:p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl lg:rounded-2xl shadow-2xl max-w-2xl w-full transform transition-all max-h-[85vh] overflow-y-auto animate-slide-up lg:animate-none">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg flex items-center justify-center shadow-md">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Create New Warehouse</h2>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <Input
                label="Warehouse Name"
                value={warehouseForm.name}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Enter warehouse name"
                required
              />
              <div>
                <Input
                  label="Location Code"
                  value={warehouseForm.code}
                  onChange={(e) => setWarehouseForm((prev) => ({ ...prev, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 5) }))}
                  placeholder="e.g. MH01"
                  required
                  maxLength={5}
                />
                {warehouseForm.code && (
                  <p className="mt-1 text-xs text-gray-500">
                    GRN will be numbered: <span className="font-mono font-semibold text-brand-600">{warehouseForm.code.toUpperCase()}-1/26-27</span>
                  </p>
                )}
              </div>
              <Input
                label="Address"
                value={warehouseForm.address}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="Enter warehouse address"
                maxLength={300}
                required
              />
              <p className="text-xs text-gray-500 -mt-3 mb-3">
                {warehouseForm.address.length}/300 characters (alphanumeric and basic punctuation only)
              </p>
              <Input
                label="Total Space (sqft)"
                type="number"
                min={String(SQFT_PER_PALLET)}
                value={warehouseForm.totalSpaceSqft}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, totalSpaceSqft: e.target.value }))}
                placeholder="Enter total square footage"
                helperText={`Must be at least ${SQFT_PER_PALLET} sqft (one pallet)`}
                required
              />

              {/* Company & Billing Details */}
              <div className="pt-4 mt-2 border-t border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Company &amp; Billing Details</h3>
                <p className="text-xs text-gray-400 mb-3">Optional — shown on GST tax invoices billed out of this warehouse.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    label="Company Name (letterhead — defaults to warehouse name)"
                    value={warehouseForm.companyName}
                    onChange={(e) => setWarehouseForm((prev) => ({ ...prev, companyName: INPUT_SANITIZERS.legalEntityNameFormat(e.target.value) }))}
                    placeholder={warehouseForm.name || 'Company name'}
                    error={warehouseBillingErrors.companyName}
                  />
                  <Input
                    label="GSTIN"
                    value={warehouseForm.companyGstin}
                    onChange={(e) => setWarehouseForm((prev) => ({ ...prev, companyGstin: INPUT_SANITIZERS.gstFormat(e.target.value) }))}
                    placeholder="27ABDFK6872E1Z5"
                    error={warehouseBillingErrors.companyGstin}
                  />
                  <Input
                    label="PAN"
                    value={warehouseForm.companyPan}
                    onChange={(e) => setWarehouseForm((prev) => ({ ...prev, companyPan: INPUT_SANITIZERS.panFormat(e.target.value) }))}
                    placeholder="ABDFK6872E"
                    error={warehouseBillingErrors.companyPan}
                  />
                  <Input
                    label="Bank Name"
                    value={warehouseForm.bankName}
                    onChange={(e) => setWarehouseForm((prev) => ({ ...prev, bankName: INPUT_SANITIZERS.legalEntityNameFormat(e.target.value) }))}
                    placeholder="HDFC BANK LTD"
                    error={warehouseBillingErrors.bankName}
                  />
                  <Input
                    label="Account No."
                    value={warehouseForm.bankAccountNo}
                    onChange={(e) => setWarehouseForm((prev) => ({ ...prev, bankAccountNo: INPUT_SANITIZERS.bankAccountFormat(e.target.value) }))}
                    placeholder="50200096136124"
                    error={warehouseBillingErrors.bankAccountNo}
                  />
                  <Input
                    label="IFSC Code"
                    value={warehouseForm.bankIfsc}
                    onChange={(e) => setWarehouseForm((prev) => ({ ...prev, bankIfsc: INPUT_SANITIZERS.ifscFormat(e.target.value).slice(0, 11) }))}
                    placeholder="HDFC0001028"
                    error={warehouseBillingErrors.bankIfsc}
                  />
                  <Input
                    label="Branch"
                    value={warehouseForm.bankBranch}
                    onChange={(e) => setWarehouseForm((prev) => ({ ...prev, bankBranch: INPUT_SANITIZERS.legalEntityNameFormat(e.target.value) }))}
                    placeholder="Seawoods"
                    error={warehouseBillingErrors.bankBranch}
                  />
                  <Input
                    label="Tagline (shown under company name)"
                    value={warehouseForm.companyTagline}
                    onChange={(e) => setWarehouseForm((prev) => ({ ...prev, companyTagline: INPUT_SANITIZERS.legalEntityNameFormat(e.target.value) }))}
                    placeholder="(Fleet Owner & Transport Contractors.)"
                    error={warehouseBillingErrors.companyTagline}
                  />
                  <Input
                    label="Company Email"
                    value={warehouseForm.companyEmail}
                    onChange={(e) => setWarehouseForm((prev) => ({ ...prev, companyEmail: INPUT_SANITIZERS.emailFormat(e.target.value) }))}
                    placeholder="accounts@example.in"
                    error={warehouseBillingErrors.companyEmail}
                  />
                  <Input
                    label="Jurisdiction City"
                    value={warehouseForm.companyJurisdiction}
                    onChange={(e) => setWarehouseForm((prev) => ({ ...prev, companyJurisdiction: INPUT_SANITIZERS.cityNameFormat(e.target.value) }))}
                    placeholder="Mumbai"
                    error={warehouseBillingErrors.companyJurisdiction}
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex space-x-3 rounded-b-2xl">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2.5 text-gray-700 bg-white hover:bg-gray-100 border border-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateWarehouse}
                disabled={
                  createWarehouseMutation.isPending ||
                  !warehouseForm.name.trim() ||
                  !warehouseForm.code.trim() ||
                  !warehouseForm.address.trim() ||
                  !warehouseForm.totalSpaceSqft ||
                  Number(warehouseForm.totalSpaceSqft) < SQFT_PER_PALLET
                }
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-md hover:shadow-lg"
              >
                {createWarehouseMutation.isPending ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Warehouse'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move / Relocate Pallet Modal */}
      {relocatingPallet && (() => {
        const relocateRack = managedRacks.find((r) => r.id === relocateRackId);
        const availableLocations = (relocateRack?.locations ?? []).filter(
          (l) => !locationToStorageMap.has(l.id) || l.id === relocatingPallet.locationId,
        );
        return (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end lg:items-center justify-center z-50 p-3 lg:p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl lg:rounded-2xl shadow-2xl max-w-md w-full transform transition-all max-h-[85vh] overflow-y-auto animate-slide-up lg:animate-none">
              <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-brand-50 to-white flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg flex items-center justify-center shadow-md">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Move Pallet</h2>
                </div>
                <button onClick={() => setRelocatingPallet(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm">
                  <div className="font-semibold text-gray-900">{relocatingPallet.productDescription || 'Product'}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {relocatingPallet.clientName} · {relocatingPallet.palletCount} pallets
                    {relocatingPallet.batchNo && <span className="font-mono"> · {relocatingPallet.batchNo}</span>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Destination type</label>
                  <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden w-full">
                    <button
                      type="button"
                      onClick={() => { setRelocateType('ground'); setRelocateRackId(null); setRelocateLocationId(null); }}
                      className={`flex-1 px-4 py-2 text-sm font-medium ${relocateType === 'ground' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600'}`}
                    >Zone (Ground)</button>
                    <button
                      type="button"
                      onClick={() => { setRelocateType('rack'); setRelocateZoneId(null); setRelocateZoneLocationId(null); }}
                      className={`flex-1 px-4 py-2 text-sm font-medium ${relocateType === 'rack' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600'}`}
                    >Rack Location</button>
                  </div>
                </div>

                {relocateType === 'ground' ? (
                  <div className="space-y-3">
                    <Select
                      label="Destination Zone"
                      options={managedZones.filter((z) => z.status === 'active').map((z) => ({ value: z.id, label: `${z.code}${z.name ? ` - ${z.name}` : ''}` }))}
                      value={relocateZoneId ?? ''}
                      onChange={(e) => { setRelocateZoneId(e.target.value ? Number(e.target.value) : null); setRelocateZoneLocationId(null); }}
                      placeholder="Choose zone"
                    />
                    {relocateZoneId != null && relocateZoneLocations.length > 0 && (
                      <Select
                        label="Zone Location (optional)"
                        options={[
                          { value: '', label: 'No specific slot' },
                          ...relocateZoneLocations
                            .filter((l) => l.status !== 'occupied' || l.id === relocatingPallet.zoneLocationId)
                            .map((l) => ({ value: l.id, label: l.code })),
                        ]}
                        value={relocateZoneLocationId ?? ''}
                        onChange={(e) => setRelocateZoneLocationId(e.target.value ? Number(e.target.value) : null)}
                        placeholder="No specific slot"
                      />
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Select
                      label="Destination Rack"
                      options={managedRacks.filter((r) => r.status === 'active').map((r) => ({ value: r.id, label: `${r.code}${r.name ? ` - ${r.name}` : ''}` }))}
                      value={relocateRackId ?? ''}
                      onChange={(e) => { setRelocateRackId(e.target.value ? Number(e.target.value) : null); setRelocateLocationId(null); }}
                      placeholder="Choose rack"
                    />
                    {relocateRackId != null && (
                      availableLocations.length === 0 ? (
                        <p className="text-sm text-amber-600">No free locations in this rack.</p>
                      ) : (
                        <Select
                          label="Destination Location (free only)"
                          options={availableLocations.map((l) => ({ value: l.id, label: l.code }))}
                          value={relocateLocationId ?? ''}
                          onChange={(e) => setRelocateLocationId(e.target.value ? Number(e.target.value) : null)}
                          placeholder="Choose location"
                        />
                      )
                    )}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex space-x-3 rounded-b-2xl">
                <button onClick={() => setRelocatingPallet(null)} className="flex-1 px-4 py-2.5 text-gray-700 bg-white hover:bg-gray-100 border border-gray-200 rounded-lg font-medium transition-colors">Cancel</button>
                <button
                  onClick={handleConfirmRelocate}
                  disabled={relocatePalletMutation.isPending || (relocateType === 'ground' ? !relocateZoneId : !relocateLocationId)}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-md"
                >
                  {relocatePalletMutation.isPending ? 'Moving…' : 'Move Pallet'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Merge Pallets — Pallet Picker Modal */}
      {showMergePicker && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end lg:items-center justify-center z-50 p-3 lg:p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl lg:rounded-2xl shadow-2xl max-w-2xl w-full transform transition-all flex flex-col max-h-[85vh] animate-slide-up lg:animate-none">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-white flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Select Pallets to Merge</h2>
                  <p className="text-xs text-gray-500">Pick two or more pallets of the same client.</p>
                </div>
              </div>
              <button onClick={clearMergeSelection} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Pallet list */}
            <div className="p-4 overflow-y-auto">
              {pallets.length === 0 ? (
                <div className="p-8 text-center text-gray-400">No stored pallets found in this warehouse.</div>
              ) : (
                <div className="space-y-2">
                  {pallets.map((p) => {
                    const checked = isSelectedForMerge(p.id);
                    const disabled = !canSelectForMerge(p);
                    const place = p.locationCode ?? p.zoneLocationCode ?? p.zoneCode ?? '—';
                    return (
                      <label
                        key={p.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${checked ? 'border-emerald-500 bg-emerald-50' : disabled ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed' : 'border-gray-200 hover:border-gray-300 cursor-pointer'}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggleMergeSelect(p)}
                          className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 disabled:cursor-not-allowed"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900 truncate">{p.productDescription || 'Product'}</span>
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-brand-100 text-brand-700">{p.palletCount} pallets</span>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{p.storageType === 'rack' ? 'Rack' : 'Ground'} · {place}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {p.clientName}
                            {p.batchNo && <span className="font-mono"> · {p.batchNo}</span>}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between rounded-b-2xl">
              <div className="text-sm text-gray-600">
                <span className="font-bold text-emerald-700">{selectedForMerge.length}</span> selected
                {mergeClientId != null && <span className="text-gray-500"> · {selectedForMerge[0].clientName}</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={clearMergeSelection} className="px-4 py-2.5 text-gray-700 bg-white hover:bg-gray-100 border border-gray-200 rounded-lg font-medium transition-colors">Cancel</button>
                <button
                  onClick={() => { setMergeTargetId(selectedForMerge[0].id); setShowMergePicker(false); setShowMergeModal(true); }}
                  disabled={selectedForMerge.length < 2}
                  className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  Continue
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Merge Pallets Modal */}
      {showMergeModal && selectedForMerge.length >= 2 && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end lg:items-center justify-center z-50 p-3 lg:p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl lg:rounded-2xl shadow-2xl max-w-lg w-full transform transition-all max-h-[85vh] overflow-y-auto animate-slide-up lg:animate-none">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Merge Pallets</h2>
                </div>
                <button onClick={() => setShowMergeModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-2 py-4 lg:p-6">
              <p className="text-sm text-gray-600 mb-4">
                Choose which pallet to <span className="font-semibold text-emerald-700">keep</span>. The others will be merged into it and their locations freed up.
              </p>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {selectedForMerge.map((p) => (
                  <label
                    key={p.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${mergeTargetId === p.id ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <input
                      type="radio"
                      name="mergeTarget"
                      checked={mergeTargetId === p.id}
                      onChange={() => setMergeTargetId(p.id)}
                      className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 truncate">{p.productDescription}</span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-brand-100 text-brand-700">{p.palletCount} pallets</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {p.clientName}
                        {p.batchNo && <span className="font-mono"> · {p.batchNo}</span>}
                        {(p.locationCode || p.zoneLocationCode || p.zoneCode) && <span> · {p.locationCode ?? p.zoneLocationCode ?? p.zoneCode}</span>}
                      </div>
                    </div>
                    {mergeTargetId === p.id && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-600 text-white flex-shrink-0">Keep</span>
                    )}
                  </label>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between text-sm bg-gray-50 rounded-lg px-4 py-3">
                <span className="text-gray-600">Combined after merge</span>
                <span className="font-bold text-emerald-700">{selectedForMerge.reduce((s, p) => s + p.palletCount, 0)} pallets in 1 location</span>
              </div>
              <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span>
                  {Math.max(0, selectedForMerge.length - 1)} source pallet record{selectedForMerge.length - 1 === 1 ? '' : 's'} will be removed and their location{selectedForMerge.length - 1 === 1 ? '' : 's'} freed. You'll have a few seconds to undo afterwards.
                </span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex space-x-3 rounded-b-2xl">
              <button
                onClick={() => setShowMergeModal(false)}
                className="flex-1 px-4 py-2.5 text-gray-700 bg-white hover:bg-gray-100 border border-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmMerge}
                disabled={mergePalletsMutation.isPending || mergeTargetId == null}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-md hover:shadow-lg"
              >
                {mergePalletsMutation.isPending ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Merging...
                  </>
                ) : (
                  `Merge ${selectedForMerge.length} Pallets`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Undo bar after a merge */}
      {pendingMerge && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] animate-fadeIn">
          <div className="flex items-center gap-4 bg-gray-900 text-white rounded-xl shadow-2xl px-5 py-3">
            <div className="flex items-center gap-2 text-sm">
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              <span>Merged into <span className="font-semibold">{pendingMerge.total} pallets</span> in 1 location</span>
            </div>
            <button
              onClick={undoPendingMerge}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-emerald-300 hover:text-emerald-200 text-sm font-semibold rounded-lg transition-colors"
            >
              Undo
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
